import React, { useRef, useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { NavigableInput } from '../ui/navigable-input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CustomDatePicker } from '../ui/custom-date-picker';
import { Truck, File as FileIcon, FileText, Clock, Receipt, Package, Loader2, Mail, Eye, Download, Lock, Sparkles, ChevronRight, ChevronUp, X, Paperclip } from 'lucide-react';
import { CLIENT_OPTIONS, preventWheelChange } from '../silica-loads';
import { SECTION_ICONS } from './sectionConfig';
import { useLoadFormContext } from './LoadFormContext';
import { AutocompleteInput } from '../ui/autocomplete-input';
import { getTodayString } from '../../utils/dateUtils';
import { toast } from 'sonner';

const SVE_API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '')}/api`;

// Simple toggle component to avoid radix-ui issues on mobile
function SimpleToggle({ checked, onChange, color = 'purple', disabled = false }) {
  const colorClasses = {
    purple: 'bg-purple-500',
    blue: 'bg-blue-500',
    indigo: 'bg-indigo-500',
    red: 'bg-red-500',
  };
  
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${checked ? colorClasses[color] : 'bg-gray-300'}`}
    >
      <span
        className={`pointer-events-none block h-4 w-4 rounded-full bg-white shadow-lg ring-0 transition-transform ${checked ? 'translate-x-4' : 'translate-x-0'}`}
      />
    </button>
  );
}

// Default values that should be treated as "empty" for border styling
const DEFAULT_VALUES = {
  delivery_status: 'in_progress',
  acceptance_status: 'pending',
  bill_status: 'pending',
  invoice_count: 1
};

// Helper function to check if a dropdown has a value selected (for black border)
const hasSelectedValue = (field, value) => {
  // For invoice_count, any valid selection (1 or 2) should show black border
  if (field === 'invoice_count') {
    return value === 1 || value === 2 || value === '1' || value === '2';
  }
  if (!value) return false;
  return true;
};

// Mobile Addition/Deduction Summary Component
function MobileAdditionDeductionSummary({ formData, handleFieldChange, summaryInvoiceFile: propFile, setSummaryInvoiceFile: propSetFile, isEditMode = false, peekedNumber, peeking, peekNextInvoiceNumber }) {
  // Local state fallback when props not provided
  const [localFile, setLocalFile] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const summaryInvoiceFile = propFile !== undefined ? propFile : localFile;
  const setSummaryInvoiceFile = propSetFile || setLocalFile;
  // Manual invoice-number override is available to super users only
  const canUseManualInvoice = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').role === 'super_approver'; }
    catch { return false; }
  }, []);
  // Track whether we have saved data from an existing load (edit mode)
  const hasSavedData = isEditMode && formData?.summary_net_amount !== undefined && formData?.summary_net_amount !== '' && formData?.summary_net_amount !== 0;
  // Track if user has manually changed dates (to trigger re-fetch in edit mode)
  const [userChangedDates, setUserChangedDates] = useState(false);
  
  const [fromDate, setFromDate] = useState(formData?.summary_from_date || '');
  const [toDate, setToDate] = useState(formData?.summary_to_date || '');
  const [loading, setLoading] = useState(false);
  const [summaryData, setSummaryData] = useState(
    // If editing and we have saved data, use it
    hasSavedData ? {
      total_additions: formData.summary_total_additions || 0,
      total_deductions: formData.summary_total_deductions || 0,
      total_addition_tons: formData.summary_total_addition_tons || 0,
      total_deduction_tons: formData.summary_total_deduction_tons || 0
    } : null
  );
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [generatedInvoice, setGeneratedInvoice] = useState(formData?.summary_generated_invoice_id ? {
    invoice_id: formData.summary_generated_invoice_id,
    invoice_number: formData.summary_generated_invoice_number,
    total_amount: formData.summary_invoice_amount
  } : null);
  
  const today = getTodayString();
  
  // Calculate net values - use formData if available, else calculate from summaryData
  const netAmount = formData?.summary_net_amount !== undefined && formData?.summary_net_amount !== '' 
    ? Number(formData.summary_net_amount) 
    : (summaryData ? (summaryData.total_additions - summaryData.total_deductions) : null);
  const netTons = formData?.summary_net_tons !== undefined && formData?.summary_net_tons !== '' 
    ? Number(formData.summary_net_tons) 
    : (summaryData ? (summaryData.total_addition_tons - summaryData.total_deduction_tons) : null);
  const isPositive = netAmount !== null && netAmount > 0;
  
  // Custom date change handlers that track user interaction
  const handleFromDateChange = (date) => {
    setFromDate(date || '');
    if (isEditMode) setUserChangedDates(true);
  };
  
  const handleToDateChange = (date) => {
    setToDate(date || '');
    if (isEditMode) setUserChangedDates(true);
  };
  
  useEffect(() => {
    if (fromDate && toDate) {
      // In edit mode with saved data, only fetch if user explicitly changed dates
      if (hasSavedData && !userChangedDates) {
        if (handleFieldChange) {
          handleFieldChange('summary_from_date', fromDate);
          handleFieldChange('summary_to_date', toDate);
        }
        return;
      }
      fetchSummary();
      if (handleFieldChange) {
        handleFieldChange('summary_from_date', fromDate);
        handleFieldChange('summary_to_date', toDate);
      }
    } else {
      setSummaryData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);
  
  useEffect(() => {
    if (summaryData && handleFieldChange && (!hasSavedData || userChangedDates)) {
      const calculatedNetAmount = summaryData.total_additions - summaryData.total_deductions;
      const calculatedNetTons = summaryData.total_addition_tons - summaryData.total_deduction_tons;
      handleFieldChange('summary_net_amount', calculatedNetAmount);
      handleFieldChange('summary_net_tons', calculatedNetTons);
      handleFieldChange('summary_total_additions', summaryData.total_additions);
      handleFieldChange('summary_total_deductions', summaryData.total_deductions);
      handleFieldChange('summary_total_addition_tons', summaryData.total_addition_tons);
      handleFieldChange('summary_total_deduction_tons', summaryData.total_deduction_tons);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryData]);
  
  const fetchSummary = async () => {
    setLoading(true);
    setError('');
    const url = `${SVE_API}/silica-loads/sve-summary?from_date=${fromDate}&to_date=${toDate}`;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        let detail = '';
        try { detail = JSON.parse(text)?.detail; } catch(e) {}
        const detailStr = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join('; ') : '';
        setError({
          operation: 'Fetch SVE Summary',
          method: 'GET',
          url,
          params: { from_date: fromDate, to_date: toDate },
          status: response.status,
          statusText: response.statusText,
          detail: detailStr,
          rawBody: text?.slice(0, 300) || '(empty)',
        });
        toast.error(`Fetch SVE Summary failed — HTTP ${response.status}`, {
          description: `URL: ${url}\nDetail: ${detailStr || text?.slice(0, 120) || '(empty)'}`,
          duration: 15000,
        });
        return;
      }
      
      const data = await response.json();
      setSummaryData(data);
    } catch (err) {
      setError({
        operation: 'Fetch SVE Summary',
        method: 'GET',
        url,
        params: { from_date: fromDate, to_date: toDate },
        status: 'Network Error',
        detail: err.message,
        rawBody: '',
      });
      toast.error(`Fetch SVE Summary — Network Error`, {
        description: `URL: ${url}\nError: ${err.message}`,
        duration: 15000,
      });
      console.error('SVE Summary fetch error:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleGenerateInvoice = async () => {
    if (!fromDate || !toDate) {
      setError('Please select both dates');
      return;
    }
    
    setGenerating(true);
    setError('');
    const genUrl = `${SVE_API}/silica-loads/generate-sve-summary-invoice`;
    
    try {
      const formDataObj = new FormData();
      formDataObj.append('from_date', fromDate);
      formDataObj.append('to_date', toDate);
      formDataObj.append('include_stamp', 'true');
      if (canUseManualInvoice && formData?.manual_summary_invoice_number) {
        formDataObj.append('manual_invoice_number', formData.manual_summary_invoice_number);
      }
      
      const response = await fetch(genUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formDataObj
      });
      
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        let detail = '';
        try { const parsed = JSON.parse(text); detail = parsed?.detail; } catch(e) {}
        const detailStr = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join('; ') : '';
        setError({
          operation: 'Generate SVE Summary Invoice',
          method: 'POST',
          url: genUrl,
          params: { from_date: fromDate, to_date: toDate, include_stamp: 'true' },
          status: response.status,
          statusText: response.statusText,
          detail: detailStr,
          rawBody: text?.slice(0, 300) || '(empty)',
        });
        toast.error(`Generate Invoice failed — HTTP ${response.status}`, {
          description: `URL: ${genUrl}\nDetail: ${detailStr || text?.slice(0, 120) || '(empty)'}`,
          duration: 15000,
        });
        setGenerating(false);
        return;
      }
      
      const data = await response.json();
      
      // Store generated invoice info
      setGeneratedInvoice({
        invoice_id: data.invoice_id,
        invoice_number: data.invoice_number,
        total_amount: data.total_amount,
        r2_key: data.r2_key
      });
      
      // Update form data with generated invoice info
      if (handleFieldChange) {
        handleFieldChange('summary_generated_invoice_id', data.invoice_id);
        handleFieldChange('summary_generated_invoice_number', data.invoice_number);
        handleFieldChange('summary_invoice_amount', data.total_amount);
        handleFieldChange('summary_r2_key', data.r2_key);
      }
      
      // Open the generated PDF in a new tab with auth headers (Blob URL)
      try {
        const token = localStorage.getItem('token');
        const viewResp = await fetch(`${SVE_API}/silica-loads/sve-summary-invoice/${data.invoice_id}/view`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (viewResp.ok) {
          const blob = await viewResp.blob();
          window.open(window.URL.createObjectURL(blob), '_blank');
        }
      } catch (viewErr) {
        console.error('Failed to open invoice:', viewErr);
      }
      
    } catch (err) {
      if (!error) {
        setError({
          operation: 'Generate SVE Summary Invoice',
          method: 'POST',
          url: genUrl,
          params: { from_date: fromDate, to_date: toDate },
          status: 'Network Error',
          detail: err.message,
          rawBody: '',
        });
      }
      toast.error(`Generate Invoice — Network Error`, {
        description: `URL: ${genUrl}\nError: ${err.message}`,
        duration: 15000,
      });
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };
  
  const handleDownloadInvoice = async () => {
    if (!generatedInvoice?.invoice_id) return;
    try {
      const response = await fetch(
        `${SVE_API}/silica-loads/sve-summary-invoice/${generatedInvoice.invoice_id}/download`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${generatedInvoice.invoice_number?.replace(/\//g, '_') || 'SVE_Summary'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error(err);
    }
  };
  
  const handleSendEmail = async () => {
    if (!generatedInvoice?.invoice_id) return;
    setSendingEmail(true);
    try {
      const response = await fetch(
        `${SVE_API}/silica-loads/sve-summary-invoice/${generatedInvoice.invoice_id}/send-email`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        const errorMsg = typeof errorData.detail === 'string' 
          ? errorData.detail 
          : Array.isArray(errorData.detail) 
            ? errorData.detail.map(e => e.msg || e.message).join(', ')
            : 'Failed to send email';
        throw new Error(errorMsg);
      }
      
    } catch (err) {
      console.error(err);
    } finally {
      setSendingEmail(false);
    }
  };
  
  const handleRemoveInvoice = async () => {
    if (!generatedInvoice?.invoice_id) return;
    
    try {
      const response = await fetch(
        `${SVE_API}/silica-loads/sve-summary-invoice/${generatedInvoice.invoice_id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to remove invoice');
      }
      
      // Clear local state
      setGeneratedInvoice(null);
      
      // Clear form data
      if (handleFieldChange) {
        handleFieldChange('summary_generated_invoice_id', '');
        handleFieldChange('summary_generated_invoice_number', '');
        handleFieldChange('summary_invoice_amount', '');
        handleFieldChange('summary_r2_key', '');
      }
    } catch (err) {
      console.error(err);
    }
  };
  
  return (
    <div className="space-y-3 mt-2">
      {/* Date Range */}
      <div className="space-y-1">
        <span className="text-xs text-slate-500 ml-1">From Date *</span>
        <CustomDatePicker 
          value={fromDate} 
          onChange={handleFromDateChange} 
          max={today}
          placeholder="Select from date" 
        />
      </div>
      <div className="space-y-1">
        <span className="text-xs text-slate-500 ml-1">To Date *</span>
        <CustomDatePicker 
          value={toDate} 
          onChange={handleToDateChange} 
          min={fromDate}
          max={today}
          placeholder="Select to date" 
        />
      </div>
      
      {loading && (
        <div className="flex items-center justify-center py-3">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          <span className="ml-2 text-sm text-slate-400">Calculating...</span>
        </div>
      )}
      
      {error && (
        typeof error === 'string' ? (
          <p className="text-sm text-red-400">{error}</p>
        ) : (
          <div className="p-3 rounded-lg bg-red-900/30 border border-red-700 text-xs font-mono space-y-1" data-testid="sve-error-detail">
            <p className="font-semibold text-red-400 text-sm">{error.operation} Failed — HTTP {error.status} {error.statusText || ''}</p>
            <p><span className="text-slate-400">Method:</span> <span className="text-red-400">{error.method}</span></p>
            <p className="break-all"><span className="text-slate-400">URL:</span> <span className="text-red-400">{error.url}</span></p>
            <p><span className="text-slate-400">Params:</span> <span className="text-red-400">{JSON.stringify(error.params)}</span></p>
            {error.detail && <p><span className="text-slate-400">Detail:</span> <span className="text-red-400">{error.detail}</span></p>}
            {error.rawBody && <p className="break-all"><span className="text-slate-400">Raw Response:</span> <span className="text-red-400">{error.rawBody}</span></p>}
          </div>
        )
      )}
      
      {/* Summary Results */}
      {summaryData && !loading && (
        <div className="space-y-3">
          {/* Net Amount Display */}
          <div className={`p-4 rounded-xl bg-white border ${isPositive ? 'border-green-500' : netAmount === 0 ? 'border-slate-300' : 'border-red-500'}`}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-xs text-slate-500">Net Amount</span>
                <p className={`text-sm font-semibold ${isPositive ? 'text-green-600' : netAmount === 0 ? 'text-slate-600' : 'text-red-600'}`}>
                  ₹{Math.abs(netAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <span className="text-xs text-slate-500">Net Tons</span>
                <p className={`text-sm font-semibold ${(netTons || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {Math.abs(netTons || 0).toFixed(3)} T
                </p>
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-slate-200 text-xs text-slate-600">
              <div>Add: ₹{(summaryData.total_additions || 0).toLocaleString('en-IN')} | {(summaryData.total_addition_tons || 0).toFixed(3)}T</div>
              <div>Ded: ₹{(summaryData.total_deductions || 0).toLocaleString('en-IN')} | {(summaryData.total_deduction_tons || 0).toFixed(3)}T</div>
            </div>
          </div>
          
          {/* Generate Invoice Section */}
          {isPositive && (
            <div className="p-3 bg-white border border-slate-200 rounded-xl space-y-3">
              {/* Generate Invoice Button */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Generate Invoice</p>
                    <p className="text-xs text-slate-500">₹{(netAmount || 0).toLocaleString('en-IN')} | {(netTons || 0).toFixed(3)}T</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!formData.summary_generated_invoice_number && peekNextInvoiceNumber && (
                      <button type="button" onClick={peekNextInvoiceNumber} disabled={peeking} className="h-9 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 text-xs font-medium" data-testid="mobile-peek-summary-btn">
                        {peeking ? '...' : peekedNumber || '#?'}
                      </button>
                    )}
                    <Button 
                      type="button" 
                      onClick={handleGenerateInvoice} 
                      disabled={generating}
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700 text-white h-9 px-4 disabled:opacity-50"
                    >
                      {generating ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-1" />
                          Generate
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                {canUseManualInvoice && (
                <div className="mt-2">
                  <Label className="text-xs text-slate-500 ml-1">Manual Invoice No. (optional)</Label>
                  <Input
                    value={formData?.manual_summary_invoice_number ?? ''}
                    onChange={(e) => handleFieldChange && handleFieldChange('manual_summary_invoice_number', e.target.value)}
                    placeholder="e.g. MJ/SS26050"
                    className="h-10 rounded-xl text-sm"
                    data-testid="mobile-manual-summary-invoice-number"
                  />
                </div>
                )}
              </div>
              
              {/* Generated Invoice Display */}
              {generatedInvoice && (
                <div className="p-3 bg-emerald-50 border border-emerald-300 rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-emerald-600" />
                      <div>
                        <p className="text-xs font-semibold text-emerald-700">{generatedInvoice.invoice_number || 'Generated'}</p>
                        <p className="text-[10px] text-emerald-600">₹{generatedInvoice.total_amount?.toLocaleString('en-IN')}</p>
                      </div>
                    </div>
                    <button 
                      type="button"
                      onClick={handleRemoveInvoice}
                      className="p-1 bg-red-100 hover:bg-red-200 text-red-600 rounded-full"
                      title="Remove Invoice"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex items-center gap-1">
                    <button 
                      type="button"
                      onClick={handleDownloadInvoice}
                      className="flex-1 px-2 py-1.5 bg-emerald-500 text-white text-[10px] rounded flex items-center justify-center gap-1"
                    >
                      <Download className="w-3 h-3" />
                      Download
                    </button>
                    <button 
                      type="button"
                      onClick={handleSendEmail}
                      disabled={sendingEmail}
                      className="flex-1 px-2 py-1.5 bg-orange-500 text-white text-[10px] rounded flex items-center justify-center gap-1 disabled:opacity-50"
                    >
                      {sendingEmail ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                      Email
                    </button>
                  </div>
                </div>
              )}
              
              {/* Upload Section - Only show if no generated invoice */}
              {!generatedInvoice && (
                <div className="space-y-1">
                  {!summaryInvoiceFile ? (
                    <label className={`flex flex-col items-center justify-center h-12 rounded-xl border-2 border-dashed cursor-pointer ${uploading ? 'bg-slate-50 border-slate-300' : 'bg-amber-50 border-amber-400'}`}>
                      <input 
                        type="file" 
                        accept=".pdf,.jpg,.jpeg,.png" 
                        disabled={uploading}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setSummaryInvoiceFile(file);
                          setUploading(true);
                          try {
                            const fd = new FormData();
                            fd.append('file', file);
                            fd.append('invoice_number', canUseManualInvoice ? (formData?.manual_summary_invoice_number || '') : '');
                            fd.append('from_date', fromDate || '');
                            fd.append('to_date', toDate || '');
                            fd.append('net_amount', netAmount || 0);
                            fd.append('total_amount', netAmount || 0);
                            const resp = await fetch(`${SVE_API}/silica-loads/upload-sve-summary-invoice`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                              body: fd
                            });
                            if (!resp.ok) { const err = await resp.json(); throw new Error(err.detail || 'Upload failed'); }
                            const data = await resp.json();
                            setGeneratedInvoice({ invoice_id: data.invoice_id, invoice_number: data.invoice_number, total_amount: data.total_amount });
                            if (handleFieldChange) {
                              handleFieldChange('summary_generated_invoice_id', data.invoice_id);
                              handleFieldChange('summary_generated_invoice_number', data.invoice_number);
                              handleFieldChange('summary_invoice_amount', data.total_amount);
                              handleFieldChange('summary_r2_key', data.r2_key);
                            }
                          } catch (err) {
                            setError(err.message || 'Upload failed');
                            setSummaryInvoiceFile(null);
                          } finally {
                            setUploading(false);
                          }
                        }} 
                        className="hidden" 
                      />
                      {uploading ? <Loader2 className="w-4 h-4 text-slate-400 animate-spin" /> : <FileText className="w-4 h-4 text-amber-500" />}
                      <span className="text-[10px] text-amber-600">{uploading ? 'Uploading...' : 'Upload Invoice (if not generating)'}</span>
                    </label>
                  ) : (
                    <div className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-300 rounded-lg">
                      <FileText className="w-4 h-4 text-emerald-600" />
                      <span className="text-xs text-emerald-700 flex-1">{summaryInvoiceFile.name}</span>
                      <button 
                        type="button"
                        onClick={() => setSummaryInvoiceFile(null)}
                        className="p-1 bg-red-100 hover:bg-red-200 text-red-600 rounded-full"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {!isPositive && (
            <div className="p-3 bg-amber-50 border border-amber-400 rounded-xl">
              <p className="text-xs text-amber-700 font-medium">Net amount is zero or negative. Invoice generation not available.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Mobile-optimized slide content for each section
function MobileBasicInfoSlide({ formData, handleFieldChange, errors, locked, onError, isEditMode, peekedNumber, peeking, peekNextInvoiceNumber }) {
  const today = getTodayString();
  
  const isBalaji = formData.client_name === 'BALAJI CASTINGS PVT LTD';
  const isSVE = formData.client_name === 'SVE CASTINGS PVT LTD';
  const sveLoadType = formData.sve_load_type || '';
  
  // Show other fields if: BALAJI selected, OR SVE with "General" selected
  const showOtherFields = isBalaji || (isSVE && sveLoadType === 'General');
  
  if (locked) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-slate-300 text-xs mb-2">
          <Lock className="w-3 h-3" />
          <span>This section is locked</span>
        </div>
        <Input value={formData.client_name} readOnly placeholder="Client" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
        {isSVE && (
          <Input value={formData.sve_load_type || ''} readOnly placeholder="Load Type" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
        )}
        {/* SVE Addition/Deduction locked view - show summary data */}
        {isSVE && sveLoadType === 'Addition/Deduction' && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <span className="text-xs text-slate-400 ml-1">From Date</span>
                <Input value={formData.summary_from_date ? new Date(formData.summary_from_date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''} readOnly className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
              </div>
              <div className="space-y-1">
                <span className="text-xs text-slate-400 ml-1">To Date</span>
                <Input value={formData.summary_to_date ? new Date(formData.summary_to_date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''} readOnly className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
              </div>
            </div>
            {formData.summary_net_amount !== undefined && formData.summary_net_amount !== '' && (
              <div className={`p-3 rounded-xl ${Number(formData.summary_net_amount) > 0 ? 'bg-green-900/30 border border-green-700' : 'bg-red-900/30 border border-red-700'}`}>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-xs text-slate-400">Net Amount</span>
                    <p className={`text-xl font-bold ${Number(formData.summary_net_amount) > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      ₹{Math.abs(Number(formData.summary_net_amount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-400">Net Tons</span>
                    <p className={`text-xl font-bold ${Number(formData.summary_net_tons || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {Math.abs(Number(formData.summary_net_tons || 0)).toFixed(3)} T
                    </p>
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t border-slate-600 grid grid-cols-2 gap-2 text-xs text-slate-400">
                  <span>Add: ₹{(formData.summary_total_additions || 0).toLocaleString('en-IN')} | {Number(formData.summary_total_addition_tons || 0).toFixed(3)}T</span>
                  <span>Ded: ₹{(formData.summary_total_deductions || 0).toLocaleString('en-IN')} | {Number(formData.summary_total_deduction_tons || 0).toFixed(3)}T</span>
                </div>
              </div>
            )}
            {formData.summary_generated_invoice_id && (
              <div className="flex items-center gap-2 p-3 bg-emerald-900/30 rounded-xl border border-emerald-700">
                <FileText className="w-5 h-5 text-emerald-400" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-emerald-300">{formData.summary_generated_invoice_number || 'Invoice'}</p>
                  <p className="text-xs text-emerald-400">₹{Number(formData.summary_invoice_amount || 0).toLocaleString('en-IN')}</p>
                </div>
              </div>
            )}
          </>
        )}
        {showOtherFields && (
          <>
            <Input value={formData.material_from} readOnly placeholder="Material From" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
            <Input value={formData.date} readOnly placeholder="Load Date" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
            <Input value={formData.date_sent} readOnly placeholder="Date Sent" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
            <Input value={formData.transport_company} readOnly placeholder="Transport Company" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
            <Input value={formData.vehicle_number} readOnly placeholder="Vehicle Number" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
            <Input value={formData.driver_name} readOnly placeholder="Driver Name" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
            <Input value={formData.driver_contact} readOnly placeholder="Driver Phone" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
            <Input value={formData.purchase_order} readOnly placeholder="Purchase Order" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Client Selection - Always shown first */}
      <div className="space-y-1">
        <span className="text-xs text-slate-500 ml-1">Select Client *</span>
        <Select value={formData.client_name} onValueChange={(v) => {
          if (v === '__ADHOC__') {
            window.dispatchEvent(new CustomEvent('open-adhoc-load-dialog'));
            return;
          }
          handleFieldChange('client_name', v);
        }}>
          <SelectTrigger id="basic-client-select" data-testid="mobile-client-select" className={`h-12 rounded-xl text-base ${formData.client_name ? 'border-slate-900' : ''}`}>
            <SelectValue placeholder="Select Client *" />
          </SelectTrigger>
          <SelectContent>
            {CLIENT_OPTIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            <SelectItem key="__ADHOC__" value="__ADHOC__" data-testid="mobile-client-select-adhoc-option">
              + Adhoc Client (one-off)
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* SVE Load Type Dropdown - Only shown when SVE is selected */}
      {isSVE && (
        <div className="space-y-1">
          <span className="text-xs text-slate-500 ml-1">Load Type *</span>
          <Select value={sveLoadType} onValueChange={(v) => handleFieldChange('sve_load_type', v)}>
            <SelectTrigger data-testid="mobile-sve-load-type-select" className={`h-12 rounded-xl text-base ${sveLoadType ? 'border-slate-900' : ''}`}>
              <SelectValue placeholder="Select Load Type *" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="General">General</SelectItem>
              <SelectItem value="Addition/Deduction">Addition/Deduction</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      
      {/* Addition/Deduction Summary - shown when SVE with Addition/Deduction selected */}
      {isSVE && sveLoadType === 'Addition/Deduction' && (
        <MobileAdditionDeductionSummary 
          formData={formData} 
          handleFieldChange={handleFieldChange}
          isEditMode={isEditMode}
          peekedNumber={peekedNumber} peeking={peeking} peekNextInvoiceNumber={peekNextInvoiceNumber}
        />
      )}
      
      {/* Other fields - shown for BALAJI or SVE with General selected */}
      {showOtherFields && (
        <>
          <NavigableInput 
            id="basic-material-from"
            value={formData.material_from} 
            onChange={(e) => handleFieldChange('material_from', e.target.value)} 
            placeholder="Material From *" 
            required 
            className="h-12 rounded-xl text-base"
            nextFieldId="basic-date-picker"
          />
          <div className="space-y-1">
            <span className="text-xs text-slate-500 ml-1">Load Date *</span>
            <CustomDatePicker
              id="basic-date-picker"
              value={formData.date}
              onChange={(date) => handleFieldChange('date', date || '')}
              max={today}
              placeholder="Select Load Date"
              label="Select Load Date"
            />
          </div>
          <div className="space-y-1">
            <span className="text-xs text-slate-500 ml-1">Date Sent *</span>
            <CustomDatePicker 
              id="basic-date-sent-picker"
              value={formData.date_sent} 
              onChange={(date) => handleFieldChange('date_sent', date || '')} 
              max={today}
              placeholder="Select Date Sent"
              label="Select Date Sent"
            />
          </div>
          <AutocompleteInput
            testId="mobile-transport-company"
            value={formData.transport_company} 
            onChange={(v) => handleFieldChange('transport_company', v)} 
            fetchUrl="/silica-loads/transport-companies"
            placeholder="Transport Company *" 
            required 
            className="h-12 rounded-xl text-base"
          />
          <NavigableInput 
            id="basic-vehicle-number"
            value={formData.vehicle_number} 
            onChange={(e) => handleFieldChange('vehicle_number', e.target.value.toUpperCase())} 
            placeholder="Vehicle Number * (AP00XX0000)" 
            required 
            className="h-12 rounded-xl text-base"
            nextFieldId="basic-driver-name"
          />
          <NavigableInput 
            id="basic-driver-name"
            value={formData.driver_name} 
            onChange={(e) => handleFieldChange('driver_name', e.target.value)} 
            placeholder="Driver Name *" 
            required 
            className="h-12 rounded-xl text-base"
            nextFieldId="basic-driver-contact"
          />
          <NavigableInput 
            id="basic-driver-contact"
            value={formData.driver_contact} 
            onChange={(e) => handleFieldChange('driver_contact', e.target.value)} 
            placeholder="Driver Phone * (10 digits)" 
            inputMode="numeric"
            pattern="[0-9]*"
            required 
            borderClassName={
              formData.driver_contact 
                ? (formData.driver_contact.replace(/\D/g, '').length === 10 
                    ? 'border-slate-900' 
                    : 'border-red-500') 
                : 'border-input'
            }
            className="h-12 rounded-xl text-base"
            nextFieldId="basic-purchase-order"
          />
          <NavigableInput 
            id="basic-purchase-order"
            value={formData.purchase_order} 
            onChange={(e) => handleFieldChange('purchase_order', e.target.value)} 
            placeholder="Purchase Order *" 
            required 
            className="h-12 rounded-xl text-base"
          />
        </>
      )}
    </div>
  );
}

function MobileStatusSlide({ formData, handleFieldChange, locked, deliveryAttachments, setDeliveryAttachments, existingDeliveryFiles, onRemoveExistingFile }) {
  const fileInputRef = React.useRef(null);
  const isRejected = formData.acceptance_status === 'rejected';
  const today = getTodayString();

  if (locked) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-slate-300 text-xs mb-2">
          <Lock className="w-3 h-3" />
          <span>This section is locked</span>
        </div>
        <Input value={formData.delivery_status === 'delivered' ? 'Delivered' : 'In Progress'} readOnly placeholder="Delivery Status" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
        <Input value={formData.date_received || ''} readOnly placeholder="Date Received" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
        <Input value={formData.acceptance_status === 'accepted' ? 'Accepted' : formData.acceptance_status === 'rejected' ? 'Rejected' : 'Pending'} readOnly placeholder="Acceptance Status" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
        {formData.acceptance_status === 'rejected' && formData.rejection_reason && (
          <Input value={formData.rejection_reason} readOnly placeholder="Rejection Reason" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <span className="text-xs text-slate-500 ml-1">Delivery Status</span>
        <Select value={formData.delivery_status} onValueChange={(v) => handleFieldChange('delivery_status', v)}>
          <SelectTrigger className={`h-12 rounded-xl text-base ${hasSelectedValue('delivery_status', formData.delivery_status) ? 'border-slate-900' : ''}`}><SelectValue placeholder="Select Delivery Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <span className="text-xs text-slate-500 ml-1">Date Received {formData.delivery_status === 'delivered' ? '*' : ''}</span>
        <CustomDatePicker 
          value={formData.date_received} 
          onChange={(date) => handleFieldChange('date_received', date || '')} 
          max={today}
          min={formData.date_sent || undefined}
          placeholder="Select Date Received"
          label="Select Date Received"
        />
      </div>
      <div className="space-y-1">
        <span className="text-xs text-slate-500 ml-1">Acceptance Status {formData.delivery_status === 'delivered' ? '*' : ''}</span>
        <Select value={formData.acceptance_status} onValueChange={(v) => handleFieldChange('acceptance_status', v)}>
          <SelectTrigger className={`h-12 rounded-xl text-base ${hasSelectedValue('acceptance_status', formData.acceptance_status) ? 'border-slate-900' : ''}`}><SelectValue placeholder="Select Acceptance Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="accepted">Accepted</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {isRejected && (
        <Textarea value={formData.rejection_reason} onChange={(e) => handleFieldChange('rejection_reason', e.target.value)} placeholder="Rejection Reason *" className={`rounded-xl text-base min-h-[80px] ${formData.rejection_reason ? 'border-slate-900' : ''}`} />
      )}
      
      {formData.delivery_status === 'delivered' && formData.date_received && formData.acceptance_status !== 'accepted' && !isRejected && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
          <p className="text-xs text-amber-700 flex items-center gap-2">
            <span>⚠️</span>
            <span><strong>Acceptance Status must be "Accepted"</strong> to proceed to next section</span>
          </p>
        </div>
      )}

      {/* Delivery Attachments — Optional */}
      <div className="space-y-1.5 pt-2 border-t border-slate-200 mt-2">
        <span className="text-xs text-slate-500 ml-1">Attachments (Optional)</span>
        {existingDeliveryFiles && existingDeliveryFiles.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {existingDeliveryFiles.map((file, i) => (
              <div key={`existing-${i}`} className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 px-2.5 py-1.5 rounded-lg">
                <span className="text-xs text-blue-700 max-w-[120px] truncate">{file.filename || `File ${i + 1}`}</span>
                {onRemoveExistingFile && (
                  <button type="button" onClick={() => onRemoveExistingFile(i)} className="text-blue-400 hover:text-red-500">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {deliveryAttachments && deliveryAttachments.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {deliveryAttachments.map((file, i) => (
              <div key={`new-${i}`} className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 rounded-lg">
                <span className="text-xs text-emerald-700 max-w-[120px] truncate">{file.name}</span>
                <button type="button" onClick={() => setDeliveryAttachments(prev => prev.filter((_, idx) => idx !== i))} className="text-emerald-400 hover:text-red-500">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        <input ref={fileInputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length > 0 && setDeliveryAttachments) setDeliveryAttachments(prev => [...prev, ...files]);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }} />
        <button type="button" onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-500 border border-dashed border-slate-300 rounded-lg hover:bg-slate-50">
          <Paperclip className="w-3.5 h-3.5" /> Add Attachment
        </button>
      </div>
    </div>
  );
}

function MobileBillingSlide({ formData, handleFieldChange, locked }) {
  const isSVEDual = formData.client_name === 'SVE CASTINGS PVT LTD' && Number(formData.invoice_count) === 2;
  
  if (locked) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-slate-300 text-xs mb-2">
          <Lock className="w-3 h-3" />
          <span>This section is locked</span>
        </div>
        {isSVEDual ? (
          <>
            <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
              <p className="text-xs font-bold text-blue-800 mb-1">Invoice 1</p>
              <Input value={(formData.bill_status_1 || 'pending').charAt(0).toUpperCase() + (formData.bill_status_1 || 'pending').slice(1)} readOnly className="h-12 rounded-xl text-base bg-slate-100 opacity-80" />
            </div>
            <div className="p-3 bg-purple-50 rounded-xl border border-purple-200">
              <p className="text-xs font-bold text-purple-800 mb-1">Invoice 2</p>
              <Input value={(formData.bill_status_2 || 'pending').charAt(0).toUpperCase() + (formData.bill_status_2 || 'pending').slice(1)} readOnly className="h-12 rounded-xl text-base bg-slate-100 opacity-80" />
            </div>
          </>
        ) : (
          <>
            <Input value={formData.bill_status === 'paid' ? 'Paid' : 'Pending'} readOnly placeholder="Bill Status" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
            {formData.bill_status === 'paid' && (
              <Input value={formData.bill_payment_date || ''} readOnly placeholder="Payment Date" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
            )}
          </>
        )}
        <Input value={formData.follow_up_email || ''} readOnly placeholder="Follow-up Email" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
        <Input value={formData.follow_up_phone || ''} readOnly placeholder="Follow-up Phone" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {isSVEDual ? (
        /* SVE Dual Invoice: Split billing */
        <>
          <div className="p-3 bg-blue-50 rounded-xl border border-blue-200 space-y-2">
            <p className="text-xs font-bold text-blue-800">Invoice 1 Billing{formData.generated_invoice_number_1 ? ` (${formData.generated_invoice_number_1})` : ''}</p>
            <div className="space-y-1">
              <span className="text-xs text-slate-500 ml-1">Bill Status</span>
              <Select value={formData.bill_status_1 || 'pending'} onValueChange={(v) => handleFieldChange('bill_status_1', v)}>
                <SelectTrigger className={`h-12 rounded-xl text-base ${hasSelectedValue('bill_status', formData.bill_status_1) ? 'border-slate-900' : ''}`}><SelectValue placeholder="Select Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.bill_status_1 === 'paid' && (
              <div className="space-y-1">
                <span className="text-xs text-slate-500 ml-1">Payment Date *</span>
                <CustomDatePicker
                  value={formData.bill_payment_date_1}
                  onChange={(date) => handleFieldChange('bill_payment_date_1', date || '')}
                  placeholder="Select Payment Date"
                  min={formData.date_received || undefined}
                  allowFuture={false}
                />
              </div>
            )}
          </div>
          <div className="p-3 bg-purple-50 rounded-xl border border-purple-200 space-y-2">
            <p className="text-xs font-bold text-purple-800">Invoice 2 Billing{formData.generated_invoice_number_2 ? ` (${formData.generated_invoice_number_2})` : ''}</p>
            <div className="space-y-1">
              <span className="text-xs text-slate-500 ml-1">Bill Status</span>
              <Select value={formData.bill_status_2 || 'pending'} onValueChange={(v) => handleFieldChange('bill_status_2', v)}>
                <SelectTrigger className={`h-12 rounded-xl text-base ${hasSelectedValue('bill_status', formData.bill_status_2) ? 'border-slate-900' : ''}`}><SelectValue placeholder="Select Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.bill_status_2 === 'paid' && (
              <div className="space-y-1">
                <span className="text-xs text-slate-500 ml-1">Payment Date *</span>
                <CustomDatePicker
                  value={formData.bill_payment_date_2}
                  onChange={(date) => handleFieldChange('bill_payment_date_2', date || '')}
                  placeholder="Select Payment Date"
                  min={formData.date_received || undefined}
                  allowFuture={false}
                />
              </div>
            )}
          </div>
          {/* Overall status indicator */}
          {(formData.bill_status_1 === 'paid' || formData.bill_status_2 === 'paid') && !(formData.bill_status_1 === 'paid' && formData.bill_status_2 === 'paid') && (
            <div className="text-xs font-semibold text-amber-600 bg-amber-50 px-3 py-2 rounded-lg border border-amber-200">
              Partially Paid — {formData.bill_status_1 === 'paid' ? 'Invoice 1 paid' : 'Invoice 2 paid'}
            </div>
          )}
        </>
      ) : (
        /* Standard single billing */
        <>
          <div className="space-y-1">
            <span className="text-xs text-slate-500 ml-1">Bill Status *</span>
            <Select value={formData.bill_status} onValueChange={(v) => handleFieldChange('bill_status', v)}>
              <SelectTrigger className={`h-12 rounded-xl text-base ${hasSelectedValue('bill_status', formData.bill_status) ? 'border-slate-900' : ''}`}><SelectValue placeholder="Select Bill Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {formData.bill_status === 'paid' && (
            <div className="space-y-1">
              <span className="text-xs text-slate-500 ml-1">Payment Date *</span>
              <CustomDatePicker
                value={formData.bill_payment_date}
                onChange={(date) => handleFieldChange('bill_payment_date', date || '')}
                placeholder="Select Payment Date"
                label="Select Payment Date"
                min={formData.date_received || undefined}
                allowFuture={false}
              />
              {!formData.bill_payment_date && <p className="text-xs text-amber-600 ml-1">Required when Bill Status is "Paid"</p>}
            </div>
          )}
        </>
      )}
      
      {/* Follow-up section */}
      <div className="pt-2 border-t border-slate-200 mt-2">
        <p className="text-xs text-slate-500 mb-2 ml-1">Follow-up Contact</p>
        <div className="space-y-3">
          <NavigableInput 
            id="billing-follow-up-email"
            type="email" 
            value={formData.follow_up_email} 
            onChange={(e) => handleFieldChange('follow_up_email', e.target.value)} 
            placeholder="Follow-up Email" 
            className="h-12 rounded-xl text-base"
            nextFieldId="billing-follow-up-phone"
          />
          <NavigableInput 
            id="billing-follow-up-phone"
            value={formData.follow_up_phone} 
            onChange={(e) => handleFieldChange('follow_up_phone', e.target.value)} 
            placeholder="Follow-up Phone (10 digits)" 
            inputMode="numeric"
            pattern="[0-9]*"
            className="h-12 rounded-xl text-base"
          />
        </div>
      </div>
    </div>
  );
}

function MobilePaymentSlide({ formData, handleFieldChange, locked }) {
  // Calculate days remaining
  const getDaysRemaining = () => {
    if (!formData.expected_payment_date) return { value: '-', status: 'neutral' };
    if (formData.bill_status === 'paid' && formData.bill_payment_date) return { value: 'Paid', status: 'paid' };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expectedDate = new Date(formData.expected_payment_date);
    expectedDate.setHours(0, 0, 0, 0);
    const daysDiff = Math.floor((expectedDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysDiff < 0) return { value: `${Math.abs(daysDiff)} days overdue`, status: 'overdue' };
    if (daysDiff === 0) return { value: 'Due today', status: 'warning' };
    if (daysDiff <= 15) return { value: `${daysDiff} days`, status: 'warning' };
    return { value: `${daysDiff} days`, status: 'ok' };
  };

  const daysRemaining = getDaysRemaining();

  if (locked) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-slate-300 text-xs mb-2">
          <Lock className="w-3 h-3" />
          <span>This section is locked</span>
        </div>
        <Input value={formData.expected_payment_date || ''} readOnly placeholder="Expected Payment Date" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
        <Input value={daysRemaining.value} readOnly placeholder="Days Remaining" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
        <Input value={formData.bill_payment_date || 'Not set'} readOnly placeholder="Payment Date" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Expected Payment Date - read only */}
      <div className="space-y-1">
        <span className="text-xs text-slate-500 ml-1">📅 Expected Payment Date</span>
        <Input value={formData.expected_payment_date || ''} readOnly placeholder="Auto-calculated" className="h-12 rounded-xl text-base bg-slate-50" />
        <p className="text-xs text-purple-600 ml-1">Auto-calculated: Date Received + {(parseInt(formData.initial_due_days) || 45) + 15} days</p>
      </div>
      
      {/* Days Remaining - read only with color coding */}
      <div className="space-y-1">
        <span className="text-xs text-slate-500 ml-1">⏱️ Days Remaining</span>
        <Input 
          value={daysRemaining.value} 
          readOnly 
          className={`h-12 rounded-xl text-base font-semibold ${
            daysRemaining.status === 'paid' ? 'bg-green-100 text-green-700' :
            daysRemaining.status === 'overdue' ? 'bg-red-100 text-red-700' :
            daysRemaining.status === 'warning' ? 'bg-yellow-100 text-yellow-700' :
            daysRemaining.status === 'ok' ? 'bg-green-100 text-green-700' :
            'bg-slate-50'
          }`}
        />
      </div>
      
      {/* Payment Date - from Billing section, read only */}
      <div className="space-y-1">
        <span className="text-xs text-slate-500 ml-1">💰 Payment Date</span>
        <Input 
          value={formData.bill_payment_date || 'Not set'} 
          readOnly 
          className={`h-12 rounded-xl text-base ${formData.bill_payment_date ? 'bg-green-100 text-green-700 font-semibold' : 'bg-slate-50 text-slate-400'}`}
        />
        {!formData.bill_payment_date && (
          <p className="text-xs text-slate-400 ml-1">Set Payment Date in Billing & Follow-up section</p>
        )}
        {formData.bill_payment_date && (
          <p className="text-xs text-green-600 ml-1">Payment completed ✓</p>
        )}
      </div>
      
      {/* Past Due indicator */}
      <div className="space-y-1">
        <span className="text-xs text-slate-500 ml-1">⚠️ Past Due</span>
        <Input 
          value={(() => {
            if (!formData.expected_payment_date) return '-';
            if (formData.bill_status === 'paid' && formData.bill_payment_date) return 'Paid';
            const today = new Date(); today.setHours(0, 0, 0, 0);
            const expectedDate = new Date(formData.expected_payment_date); expectedDate.setHours(0, 0, 0, 0);
            return today > expectedDate ? 'Yes' : 'No';
          })()} 
          readOnly 
          className={`h-12 rounded-xl text-base font-semibold ${(() => {
            if (!formData.expected_payment_date) return 'bg-slate-50';
            if (formData.bill_status === 'paid' && formData.bill_payment_date) return 'bg-green-100 text-green-700';
            const today = new Date(); today.setHours(0, 0, 0, 0);
            const expectedDate = new Date(formData.expected_payment_date); expectedDate.setHours(0, 0, 0, 0);
            return today > expectedDate ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700';
          })()}`}
        />
      </div>
    </div>
  );
}

// Main Mobile Carousel View Component
export default function MobileCarouselView({ contextValue }) {
  const {
    formData, setFormData, handleFieldChange, errors,
    editingLoad, isEditMode, isSubmitting, setErrorDialog,
    ALL_SECTIONS, currentSlideIndex, handleSlideChange,
    isSectionEnabled, isSectionComplete, isSectionLocked, isSectionVisible,
    handleCancelClick,
    // Invoice files
    invoiceFile, setInvoiceFile, permitFile, setPermitFile, otherBills, setOtherBills,
    invoiceFile1, setInvoiceFile1, invoiceFile2, setInvoiceFile2,
    permitFile1, setPermitFile1, permitFile2, setPermitFile2,
    otherBills1, setOtherBills1, otherBills2, setOtherBills2,
    // Updated/Addition/Deduction files
    updatedInvoiceFile, setUpdatedInvoiceFile,
    additionInvoiceFile, setAdditionInvoiceFile,
    deductionInvoiceFile, setDeductionInvoiceFile,
    // File removal flags
    removeInvoiceFile, setRemoveInvoiceFile,
    removePermitFile, setRemovePermitFile,
    removeUpdatedInvoiceFile, setRemoveUpdatedInvoiceFile,
    removeAdditionInvoiceFile, setRemoveAdditionInvoiceFile,
    removeDeductionInvoiceFile, setRemoveDeductionInvoiceFile,
    // Invoice generation
    generating, generating1, generating2, generatingUpdated, generatingAddition, generatingDeduction,
    handleGenerateInvoiceClick, handleGenerateInvoice1Click, handleGenerateInvoice2Click,
    handleGenerateUpdatedInvoiceClick, handleGenerateAdditionInvoiceClick, handleGenerateDeductionInvoiceClick,
    handleSendEmailClick,
    dialogContentRef,
    scrollAnchorRef,
    manualInvoiceErrors, clearManualInvoiceError,
    peekedNumber, peeking, peekNextInvoiceNumber,
    deliveryAttachments, setDeliveryAttachments, existingDeliveryFiles, setExistingDeliveryFiles,
  } = contextValue;

  // Create onError callback for date validation errors
  const handleDateError = (message, fieldName, fieldId) => {
    if (setErrorDialog) {
      setErrorDialog({ open: true, message, fieldName, fieldId });
    }
  };

  const carouselRef = useRef(null);
  const outerRef = useRef(null);
  const isMountedRef = useRef(false);
  const openTimeRef = useRef(Date.now());
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);

  // After the mobileSlideUp animation (750ms), reset any iOS-preserved scroll.
  useEffect(() => {
    const t = setTimeout(() => {
      if (dialogContentRef?.current) dialogContentRef.current.scrollTop = 0;
    }, 1000);
    return () => clearTimeout(t);
  }, []);

  // Smooth scroll to top on section change
  // Wait for horizontal slide to complete (450ms), then scroll
  useEffect(() => {
    if (!isMountedRef.current) { isMountedRef.current = true; return; }
    const t = setTimeout(() => {
      outerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 450);
    return () => clearTimeout(t);
  }, [currentSlideIndex]);

  // Get only visible and enabled/locked sections for the carousel
  // Locked sections should still be viewable (just read-only)
  const visibleSections = ALL_SECTIONS.filter(s => isSectionVisible(s));
  const enabledSections = visibleSections.filter(s => isSectionEnabled(s) || isSectionLocked(s));
  const currentSection = ALL_SECTIONS[currentSlideIndex];
  const currentEnabledIndex = enabledSections.indexOf(currentSection);
  // If section not found in enabledSections, show the last enabled section instead of jumping to first
  const actualIndex = currentEnabledIndex >= 0 ? currentEnabledIndex : Math.max(0, enabledSections.length - 1);

  // Check if Add/Update button should be enabled
  // For SVE Addition/Deduction mode, allow submit from basic section when complete
  const isSVEAdditionDeduction = formData.client_name === 'SVE CASTINGS PVT LTD' && formData.sve_load_type === 'Addition/Deduction';
  const allowedSectionsForSubmit = isSVEAdditionDeduction 
    ? ['basic', 'billing'] 
    : ['invoice', 'updated_invoice', 'weight_adjustment', 'billing'];
  const isAllowedSection = allowedSectionsForSubmit.includes(currentSection);
  const isButtonEnabled = isAllowedSection && isSectionComplete(currentSection);
  
  // Scroll to top when Add/Update button becomes enabled
  useEffect(() => {
    if (isButtonEnabled) {
      // Scroll to top to show the Add/Update button
      outerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isButtonEnabled]);

  const renderSlideContent = (section) => {
    const locked = isSectionLocked(section);
    const complete = isSectionComplete(section);

    switch (section) {
      case 'basic':
        return (
          <MobileBasicInfoSlide 
            formData={formData} 
            handleFieldChange={handleFieldChange} 
            errors={errors}
            locked={locked}
            onError={handleDateError}
            isEditMode={isEditMode}
            peekedNumber={peekedNumber} peeking={peeking} peekNextInvoiceNumber={peekNextInvoiceNumber}
          />
        );
      case 'status':
        return (
          <MobileStatusSlide 
            formData={formData} 
            handleFieldChange={handleFieldChange} 
            locked={locked}
            deliveryAttachments={deliveryAttachments}
            setDeliveryAttachments={setDeliveryAttachments}
            existingDeliveryFiles={existingDeliveryFiles}
            onRemoveExistingFile={(index) => setExistingDeliveryFiles(prev => prev.filter((_, i) => i !== index))}
          />
        );
      case 'billing':
        return (
          <MobileBillingSlide 
            formData={formData} 
            handleFieldChange={handleFieldChange} 
            locked={locked} 
          />
        );
      case 'payment':
        return (
          <MobilePaymentSlide 
            formData={formData} 
            handleFieldChange={handleFieldChange} 
            locked={locked} 
          />
        );
      case 'invoice':
        return (
          <MobileInvoiceSlide 
            contextValue={contextValue}
            locked={locked} 
          />
        );
      case 'updated_invoice':
        return (
          <MobileUpdatedInvoiceSlide 
            contextValue={contextValue}
            locked={locked} 
          />
        );
      case 'weight_adjustment':
        return (
          <MobileWeightAdjustmentSlide 
            contextValue={contextValue}
            locked={locked} 
          />
        );
      default:
        return null;
    }
  };

  const getSectionHeaderStyle = (section) => {
    const locked = isSectionLocked(section);
    const complete = isSectionComplete(section);
    
    if (locked) return { bg: 'bg-slate-800 border-slate-700', text: 'text-white' };
    if (complete) return { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-600' };
    return { bg: 'bg-white border-slate-200', text: 'text-slate-600' };
  };

  const getSectionIcon = (section) => {
    const icons = {
      basic: Truck,
      invoice: Receipt,
      status: Package,
      updated_invoice: FileText,
      weight_adjustment: Receipt,
      billing: Receipt,
      payment: Clock,
    };
    return icons[section] || Receipt;
  };

  const getSectionTitle = (section) => {
    const titles = {
      basic: 'Basic Information',
      invoice: 'Invoice Details',
      status: 'Delivery & Status',
      updated_invoice: 'Updated Invoice',
      weight_adjustment: 'Weight Adjustment',
      billing: 'Billing & Follow-up',
      payment: 'Payment Tracking',
    };
    return titles[section] || section;
  };

  return (
    <div ref={outerRef} className="mobile-load-enter">
      {/* iOS-style header with actions - with safe area padding for notch */}
      <div className="bg-white border-b border-slate-200 px-4 pb-3 flex items-center justify-between mobile-header-enter" style={{ paddingTop: 'max(16px, env(safe-area-inset-top, 16px))' }}>
        <Button
          type="button"
          onClick={handleCancelClick}
          className="bg-red-400 hover:bg-red-500 text-white w-20 py-1.5 rounded-lg text-sm font-medium h-auto"
        >
          Cancel
        </Button>
        <span className="font-semibold text-slate-800 text-sm">
          {editingLoad?.load_id ? 'Edit Silica Load' : 'Add Silica Load'}
        </span>
        <div className="flex items-center gap-2">
          {isSectionLocked(currentSection) ? (
            <div className="w-20" />
          ) : (
            <Button 
              type="submit"
              disabled={isSubmitting || !isButtonEnabled}
              className={`w-20 py-1.5 rounded-lg text-sm font-medium h-auto text-white ${
                !isButtonEnabled 
                  ? 'bg-slate-400 cursor-not-allowed' 
                  : 'bg-emerald-500 hover:bg-emerald-400 button-blink'
              }`}
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                editingLoad?.load_id ? 'Update' : 'Add'
              )}
            </Button>
          )}
        </div>
      </div>
      
      {/* Progress bar below header */}
      <div className="bg-white px-4 py-3 mx-1 mobile-progress-enter">
        <div className="relative flex justify-center items-center gap-3">
          {ALL_SECTIONS.filter(section => isSectionVisible(section)).map((section, idx) => {
            const isEnabled = isSectionEnabled(section);
            const isComplete = isSectionComplete(section);
            const isLocked = isSectionLocked(section);
            const isCurrent = ALL_SECTIONS[currentSlideIndex] === section;
            
            // Find if this is the next incomplete section (should pulse/blink)
            const visibleSections = ALL_SECTIONS.filter(s => isSectionVisible(s));
            const currentSectionIndex = visibleSections.indexOf(ALL_SECTIONS[currentSlideIndex]);
            const thisSectionIndex = visibleSections.indexOf(section);
            const isNextIncomplete = !isComplete && !isLocked && isEnabled && 
                                     thisSectionIndex === currentSectionIndex + 1;
            
            const IconComponent = SECTION_ICONS[section];
            
            return (
              <button
                key={section}
                type="button"
                onClick={() => (isEnabled || isLocked) && handleSlideChange(ALL_SECTIONS.indexOf(section))}
                disabled={!isEnabled && !isLocked}
                style={{ animationDelay: `${0.12 + idx * 0.04}s` }}
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 mobile-dot-enter
                  ${isLocked
                    ? 'bg-slate-800 text-white'
                    : isComplete 
                      ? 'bg-emerald-500 text-white' 
                      : isEnabled 
                        ? 'bg-red-400 text-white' 
                        : 'bg-slate-300 text-slate-500'}
                  ${isCurrent ? 'ring-2 ring-offset-2 ring-slate-400 scale-110' : ''}
                  ${isNextIncomplete ? 'animate-pulse ring-2 ring-red-300' : ''}
                `}
              >
                {isLocked ? <Lock className="w-3.5 h-3.5" /> : <IconComponent className="w-3.5 h-3.5" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Carousel */}
      <div 
        ref={carouselRef}
        className="overflow-hidden relative"
        onTouchStart={(e) => {
          window._touchStartX = e.touches[0].clientX;
          window._touchStartTime = Date.now();
          setIsSwiping(true);
        }}
        onTouchMove={(e) => {
          if (!isSwiping) return;
          const currentX = e.touches[0].clientX;
          let diff = currentX - window._touchStartX;
          
          // Check if trying to swipe to next section (left swipe = positive diff from start)
          const isSwipingLeft = diff < 0; // Swiping to see next section
          const isSwipingRight = diff > 0; // Swiping to see previous section
          
          // Check if next/previous section is enabled
          const canGoNext = actualIndex < enabledSections.length - 1;
          const canGoPrev = actualIndex > 0;
          
          // Apply heavy resistance if trying to swipe to disabled direction
          if ((isSwipingLeft && !canGoNext) || (isSwipingRight && !canGoPrev)) {
            // Strong resistance - only allow 20% of swipe movement (rubber band effect)
            diff = diff * 0.2;
          } else {
            // Even for enabled sections, add some resistance (60% of movement)
            diff = diff * 0.6;
          }
          
          setSwipeOffset(diff);
        }}
        onTouchEnd={(e) => {
          const touchEndX = e.changedTouches[0].clientX;
          const diff = window._touchStartX - touchEndX;
          const timeTaken = Date.now() - window._touchStartTime;
          const velocity = Math.abs(diff) / timeTaken;
          
          // Much stricter thresholds: require 120px swipe OR fast velocity (0.5) with at least 80px
          const hasEnoughDistance = Math.abs(diff) > 120;
          const hasFastSwipe = velocity > 0.5 && Math.abs(diff) > 80;
          
          if (hasEnoughDistance || hasFastSwipe) {
            if (diff > 0 && actualIndex < enabledSections.length - 1) {
              // Swiping left to go to next section
              const nextSection = enabledSections[actualIndex + 1];
              const nextIndex = ALL_SECTIONS.indexOf(nextSection);
              handleSlideChange(nextIndex);
            } else if (diff < 0 && actualIndex > 0) {
              // Swiping right to go to previous section
              const prevSection = enabledSections[actualIndex - 1];
              const prevIndex = ALL_SECTIONS.indexOf(prevSection);
              handleSlideChange(prevIndex);
            }
          }
          
          setSwipeOffset(0);
          setIsSwiping(false);
        }}
      >
        <div className="flex flex-col">
          <div 
            className={`flex mobile-carousel-track ${isSwiping ? 'swiping' : ''}`}
            style={{
              transform: `translateX(calc(-${actualIndex * 100}% + ${swipeOffset}px))`
            }}
          >
            {enabledSections.map((section) => {
              const IconComponent = getSectionIcon(section);
              const { bg, text } = getSectionHeaderStyle(section);
              const locked = isSectionLocked(section);
              const complete = isSectionComplete(section);
              const isCurrentSection = enabledSections[actualIndex] === section;
              const isLastSection = enabledSections.indexOf(section) === enabledSections.length - 1;
              const showSwipeHint = isCurrentSection && complete && !isLastSection;

              return (
                <div key={section} className="w-full flex-shrink-0 px-1 relative" data-slide-index={ALL_SECTIONS.indexOf(section)}>
                  <div className={`rounded-2xl border shadow-sm p-4 ${bg} mobile-card-enter relative overflow-hidden`}>
                    <div className={`text-sm font-semibold ${text} mb-3 flex items-center gap-2`}>
                      <IconComponent className="w-4 h-4" />
                      {getSectionTitle(section)}
                      {locked && <Lock className="w-3 h-3 text-slate-400 ml-1" />}
                      {complete && <span className="ml-auto text-emerald-600 text-xs">✓ Complete</span>}
                    </div>
                    {renderSlideContent(section)}
                    
                    {/* Swipe Right Hint - Bottom right arrows */}
                    {showSwipeHint && (
                      <div className="absolute -bottom-1 right-3 pointer-events-none flex items-center swipe-hint-animation">
                        <ChevronRight className="w-6 h-6 text-slate-800 chevron-bounce" />
                        <ChevronRight className="w-6 h-6 text-slate-600 chevron-bounce delay-1 -ml-3" />
                        <ChevronRight className="w-6 h-6 text-slate-400 chevron-bounce delay-2 -ml-3" />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Slide Indicators */}
          <div className="flex justify-center gap-1.5 mt-3 mobile-indicators-enter">
            {enabledSections.map((section, index) => (
              <button
                key={section}
                type="button"
                onClick={() => handleSlideChange(ALL_SECTIONS.indexOf(section))}
                className={`h-1.5 rounded-full transition-all ${
                  index === actualIndex 
                    ? 'w-6 bg-slate-700' 
                    : isSectionComplete(section)
                      ? 'w-1.5 bg-emerald-400'
                      : 'w-1.5 bg-slate-300 hover:bg-slate-400'
                }`}
              />
            ))}
          </div>
          
          {/* Critical Section Hint */}
          {(() => {
            const currentSection = enabledSections[actualIndex];
            const isComplete = isSectionComplete(currentSection);
            const criticalSections = ['invoice', 'updated_invoice', 'weight_adjustment', 'billing'];
            const isCriticalSection = criticalSections.includes(currentSection);
            
            if (isCriticalSection && !isComplete) {
              const hintText = currentSection === 'invoice' 
                ? '✨ Fill to save'
                : currentSection === 'updated_invoice'
                ? '📋 Required'
                : currentSection === 'weight_adjustment'
                ? '⚖️ Required'
                : '✅ Almost done';
              return (
                <div className="flex items-center justify-center mt-2">
                  <div className="px-3 py-1 rounded-full bg-gradient-to-r from-amber-100 to-orange-100 border border-amber-200 pulse-glow">
                    <span className="text-xs font-medium text-amber-700">{hintText}</span>
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </div>
      </div>
    </div>
  );
}

// Complex slide components that need many props
function MobileInvoiceSlide({ contextValue, locked }) {
  const {
    formData, setFormData, handleFieldChange, editingLoad,
    invoiceFile, setInvoiceFile, permitFile, setPermitFile, otherBills, setOtherBills,
    invoiceFile1, setInvoiceFile1, invoiceFile2, setInvoiceFile2,
    permitFile1, setPermitFile1, permitFile2, setPermitFile2,
    otherBills1, setOtherBills1, otherBills2, setOtherBills2,
    removeInvoiceFile, setRemoveInvoiceFile,
    removePermitFile, setRemovePermitFile,
    generating, generating1, generating2,
    handleGenerateInvoiceClick, handleGenerateInvoice1Click, handleGenerateInvoice2Click,
    handleSendEmailClick,
    manualInvoiceErrors, clearManualInvoiceError,
    peekedNumber, peeking, peekNextInvoiceNumber,
  } = contextValue;

  // Toggle states for generate buttons - must be before any early returns
  const [generateEnabled, setGenerateEnabled] = useState(false);
  const [generate1Enabled, setGenerate1Enabled] = useState(false);
  const [generate2Enabled, setGenerate2Enabled] = useState(false);

  // Manual invoice-number override is available to super users only
  const canUseManualInvoice = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').role === 'super_approver'; }
    catch { return false; }
  }, []);

  if (locked) {
    const isBalaji = formData.client_name === 'BALAJI CASTINGS PVT LTD';
    const isSVE = formData.client_name === 'SVE CASTINGS PVT LTD';
    const isMultiInvoiceClient = isBalaji || isSVE;
    const isTwoInvoiceMode = isMultiInvoiceClient && Number(formData.invoice_count) === 2;
    
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-slate-300 text-xs mb-2">
          <Lock className="w-3 h-3" />
          <span>This section is locked</span>
        </div>
        
        {/* Show invoice count for multi-invoice clients */}
        {isMultiInvoiceClient && (
          <Input value={`${formData.invoice_count || 1} Invoice(s)`} readOnly className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
        )}
        
        {/* Common fields */}
        <Input value={formData.product} readOnly placeholder="Product" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
        <Input value={formData.rate} readOnly placeholder="Rate" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
        
        {/* Single invoice mode display */}
        {!isTwoInvoiceMode && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <Input value={formData.kgs} readOnly placeholder="Kgs" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
              <Input value={formData.tons || ''} readOnly placeholder="Tons" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
            </div>
            <Input value={formData.invoice_amount ? `₹${formData.invoice_amount}` : ''} readOnly placeholder="Invoice Amount" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80 font-semibold" />
            {formData.generated_invoice_number && (
              <p className="text-xs text-slate-300">Invoice: {formData.generated_invoice_number}</p>
            )}
          </>
        )}
        
        {/* Two invoice mode display */}
        {isTwoInvoiceMode && (
          <>
            {/* Totals Summary */}
            <div className="p-2 bg-indigo-900/50 rounded-lg border border-indigo-700">
              <p className="text-[10px] font-semibold text-indigo-300">
                Total: {formData.kgs || '0'} Kgs | {formData.tons || '0'} Tons | ₹{formData.invoice_amount || '0'}
              </p>
            </div>
            
            {/* Invoice 1 */}
            <div className="p-2 bg-blue-900/50 rounded-lg border border-blue-700">
              <p className="text-xs font-bold text-blue-300 mb-1">Invoice 1</p>
              <div className="grid grid-cols-2 gap-1 text-xs text-slate-300">
                <span>Kgs: {formData.kgs_1 || '-'}</span>
                <span>Tons: {formData.tons_1 || '-'}</span>
              </div>
              <p className="text-xs text-slate-300">Amount: ₹{formData.invoice_amount_1 || '-'}</p>
              {formData.generated_invoice_number_1 && (
                <p className="text-[10px] text-blue-300 mt-1">Invoice #: {formData.generated_invoice_number_1}</p>
              )}
            </div>
            
            {/* Invoice 2 */}
            <div className="p-2 bg-purple-900/50 rounded-lg border border-purple-700">
              <p className="text-xs font-bold text-purple-300 mb-1">Invoice 2</p>
              <div className="grid grid-cols-2 gap-1 text-xs text-slate-300">
                <span>Kgs: {formData.kgs_2 || '-'}</span>
                <span>Tons: {formData.tons_2 || '-'}</span>
              </div>
              <p className="text-xs text-slate-300">Amount: ₹{formData.invoice_amount_2 || '-'}</p>
              {formData.generated_invoice_number_2 && (
                <p className="text-[10px] text-purple-300 mt-1">Invoice #: {formData.generated_invoice_number_2}</p>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  const isBalaji = formData.client_name === 'BALAJI CASTINGS PVT LTD';
  const isSVE = formData.client_name === 'SVE CASTINGS PVT LTD';
  const isMultiInvoiceClient = isBalaji || isSVE;
  const isTwoInvoiceMode = isMultiInvoiceClient && Number(formData.invoice_count) === 2;

  return (
    <div className="space-y-3">
      {/* Number of Invoices dropdown - BALAJI and SVE */}
      {isMultiInvoiceClient && (
        <div className="space-y-1">
          <span className="text-xs text-slate-500 ml-1">Number of Invoices *</span>
          <Select value={String(formData.invoice_count || '')} onValueChange={(v) => handleFieldChange('invoice_count', v)}>
            <SelectTrigger className={`h-12 rounded-xl text-base ${hasSelectedValue('invoice_count', formData.invoice_count) ? 'border-slate-900' : ''}`}>
              <SelectValue placeholder="Select Number of Invoices *" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 Invoice</SelectItem>
              <SelectItem value="2">2 Invoices</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Show remaining fields only after invoice_count is selected (for multi-invoice clients) or always (for other clients) */}
      {(!isMultiInvoiceClient || formData.invoice_count) && (
        <>
          {/* Shared fields: Product and Rate */}
          <NavigableInput 
            id="invoice-product"
            value={formData.product} 
            onChange={(e) => handleFieldChange('product', e.target.value)} 
            placeholder="Product *" 
            className="h-12 rounded-xl text-base"
            nextFieldId="invoice-rate"
          />
          <NavigableInput 
            id="invoice-rate"
            type="number" 
            value={formData.rate} 
            onChange={(e) => handleFieldChange('rate', e.target.value)} 
            onWheel={preventWheelChange} 
            placeholder="Rate (₹/Kg) *" 
            className="h-12 rounded-xl text-base"
            nextFieldId="invoice-kgs"
          />
      
          {/* SINGLE INVOICE MODE */}
          {!isTwoInvoiceMode && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <NavigableInput 
                  id="invoice-kgs"
                  type="number" 
                  value={formData.kgs} 
                  onChange={(e) => handleFieldChange('kgs', e.target.value)} 
                  onWheel={preventWheelChange} 
                  placeholder="Quantity (Kgs) *" 
                  className="h-12 rounded-xl text-base"
                />
                <Input 
                  type="number" 
                  value={formData.tons} 
                  onChange={(e) => handleFieldChange('tons', e.target.value)} 
                  onWheel={preventWheelChange} 
                  placeholder="Tons" 
                  className="h-12 rounded-xl text-base" 
                />
              </div>
              
              {/* Auto-calculated Invoice Amount */}
              <div className="space-y-1">
                <span className="text-xs text-slate-500 ml-1">Invoice Amount (Auto-calculated with 5% GST)</span>
                <div className={`h-12 rounded-xl text-base bg-gradient-to-r ${formData.invoice_amount ? 'from-green-50 to-emerald-50 border-green-300' : 'from-slate-50 to-slate-100 border-slate-200'} border flex items-center px-3 font-semibold ${formData.invoice_amount ? 'text-green-700' : 'text-slate-400'}`}>
                  {formData.invoice_amount ? `₹${Number(formData.invoice_amount).toLocaleString('en-IN')}` : 'Enter Kgs & Rate'}
                </div>
              </div>
          
              {/* Generate Invoice Button with Toggle */}
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <SimpleToggle 
                    checked={generateEnabled} 
                    onChange={setGenerateEnabled} 
                    color="purple" 
                  />
                  <span className="text-xs text-slate-600">Enable Invoice Generation</span>
                </div>
                <div className={`space-y-1 ${canUseManualInvoice ? '' : 'hidden'}`} data-testid="mobile-manual-invoice-wrapper">
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Manual Invoice No. (optional)</p>
                <Input
                  value={formData.manual_invoice_number ?? ''}
                  onChange={(e) => {
                    handleFieldChange('manual_invoice_number', e.target.value);
                    if (clearManualInvoiceError) clearManualInvoiceError('manual_invoice_number');
                  }}
                  placeholder="e.g. MJ/SS25001"
                  className={`h-12 rounded-xl text-base ${manualInvoiceErrors?.manual_invoice_number ? 'border-red-500' : ''}`}
                  data-testid="mobile-manual-invoice-number"
                />
                {manualInvoiceErrors?.manual_invoice_number && (
                  <p className="text-xs text-red-600 leading-tight">{manualInvoiceErrors.manual_invoice_number}</p>
                )}
                </div>
              <div className="flex gap-2">
                {!formData.generated_invoice_number && (
                <button type="button" onClick={peekNextInvoiceNumber} disabled={peeking} className="h-12 px-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 text-sm font-medium shrink-0" data-testid="mobile-peek-next-number-btn">
                  {peeking ? '...' : peekedNumber || '#?'}
                </button>
                )}
                <button
                  type="button"
                  onClick={handleGenerateInvoiceClick}
                  disabled={!generateEnabled || !formData.product || !formData.kgs || !formData.rate || generating}
                  className={`w-full h-12 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md ${
                    formData.generated_invoice_file 
                      ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white' 
                      : 'bg-gradient-to-r from-red-400 to-red-500 hover:from-red-500 hover:to-red-600 text-white'
                  }`}
                >
                  {generating ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      <span>{formData.generated_invoice_file ? 'Regen INV' : 'Gen INV'}</span>
                    </>
                  )}
                </button>
              </div>
              </div>
          
              {/* Show generated invoice actions */}
              {formData.generated_invoice_file && (
                <>
                  <div className="flex items-center gap-2 p-3 bg-green-100 rounded-xl border border-green-300">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-green-800 truncate">{formData.generated_invoice_number}</p>
                      <p className="text-[10px] text-green-600">₹{formData.generated_invoice_amount}</p>
                    </div>
                    <div className="flex gap-1.5">
                      <a href={formData.generated_invoice_file} target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors" title="View PDF">
                        <Eye className="w-4 h-4" />
                      </a>
                      <a href={formData.generated_invoice_file} download={`${formData.generated_invoice_number?.replace('PREVIEW-', '') || 'invoice'}.pdf`} className="w-9 h-9 flex items-center justify-center bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors" title="Download">
                        <Download className="w-4 h-4" />
                      </a>
                      <button type="button" onClick={() => handleSendEmailClick(formData.generated_invoice_number, formData.generated_invoice_amount, 'regular')} className="w-9 h-9 flex items-center justify-center bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors" title="Send Email">
                        <Mail className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-end mt-1">
                    <button type="button" onClick={() => { setInvoiceFile(null); setFormData(prev => ({ ...prev, generated_invoice_file: '', generated_invoice_number: '', generated_invoice_amount: '' })); }} className="text-[10px] text-red-500 hover:text-red-700 underline">
                      Remove Generated Invoice
                    </button>
                  </div>
                </>
              )}
          
              {/* File upload buttons - Single Invoice Mode */}
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <label className={`flex flex-col items-center justify-center h-16 rounded-xl border-2 border-dashed cursor-pointer transition-all ${invoiceFile || (!removeInvoiceFile && editingLoad?.invoice_filename) || formData.generated_invoice_file ? 'bg-emerald-50 border-emerald-400' : 'bg-red-50 border-red-300 hover:border-red-400'}`}>
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setInvoiceFile(e.target.files[0])} className="hidden" />
                    <FileText className={`w-5 h-5 ${invoiceFile || (!removeInvoiceFile && editingLoad?.invoice_filename) || formData.generated_invoice_file ? 'text-emerald-600' : 'text-red-500'}`} />
                    <span className={`text-[10px] mt-1 ${invoiceFile || (!removeInvoiceFile && editingLoad?.invoice_filename) || formData.generated_invoice_file ? 'text-emerald-600' : 'text-red-500'}`}>Invoice *</span>
                  </label>
                  {invoiceFile && (
                    <div className="flex items-center justify-between bg-emerald-50 rounded-lg px-2 py-1">
                      <p className="text-[9px] text-emerald-600 truncate flex-1">{invoiceFile.name}</p>
                      <button type="button" onClick={() => { setInvoiceFile(null); setFormData(prev => ({ ...prev, generated_invoice_file: '', generated_invoice_number: '', generated_invoice_amount: '' })); }} className="text-[9px] text-red-500 ml-1">✕</button>
                    </div>
                  )}
                  {!invoiceFile && formData.generated_invoice_file && (
                    <div className="flex items-center justify-between bg-green-50 rounded-lg px-2 py-1">
                      <p className="text-[9px] text-green-600 truncate flex-1">{formData.generated_invoice_number || 'Generated Invoice'}</p>
                      <button type="button" onClick={() => setFormData(prev => ({ ...prev, generated_invoice_file: '', generated_invoice_number: '', generated_invoice_amount: '' }))} className="text-[9px] text-red-500 ml-1">✕</button>
                    </div>
                  )}
                </div>
                
                <div className="space-y-1">
                  <label className={`flex flex-col items-center justify-center h-16 rounded-xl border-2 border-dashed cursor-pointer transition-all ${permitFile || (!removePermitFile && editingLoad?.permit_filename) ? 'bg-emerald-50 border-emerald-400' : 'bg-red-50 border-red-300 hover:border-red-400'}`}>
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setPermitFile(e.target.files[0])} className="hidden" />
                    <FileIcon className={`w-5 h-5 ${permitFile || (!removePermitFile && editingLoad?.permit_filename) ? 'text-emerald-600' : 'text-red-500'}`} />
                    <span className={`text-[10px] mt-1 ${permitFile || (!removePermitFile && editingLoad?.permit_filename) ? 'text-emerald-600' : 'text-red-500'}`}>Permit *</span>
                  </label>
                  {permitFile && (
                    <div className="flex items-center justify-between bg-emerald-50 rounded-lg px-2 py-1">
                      <p className="text-[9px] text-emerald-600 truncate flex-1">{permitFile.name}</p>
                      <button type="button" onClick={() => setPermitFile(null)} className="text-[9px] text-red-500 ml-1">✕</button>
                    </div>
                  )}
                </div>
                
                <div className="space-y-1">
                  <label className={`flex flex-col items-center justify-center h-16 rounded-xl border-2 border-dashed cursor-pointer transition-all ${(otherBills && otherBills.length > 0) || (editingLoad?.other_bills_filenames && editingLoad.other_bills_filenames.length > 0) ? 'bg-emerald-50 border-emerald-400' : 'bg-slate-50 border-slate-300 hover:border-slate-400'}`}>
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" multiple onChange={(e) => setOtherBills(Array.from(e.target.files))} className="hidden" />
                    <Receipt className={`w-5 h-5 ${(otherBills && otherBills.length > 0) || (editingLoad?.other_bills_filenames && editingLoad.other_bills_filenames.length > 0) ? 'text-emerald-600' : 'text-slate-400'}`} />
                    <span className={`text-[10px] mt-1 ${(otherBills && otherBills.length > 0) || (editingLoad?.other_bills_filenames && editingLoad.other_bills_filenames.length > 0) ? 'text-emerald-600' : 'text-slate-400'}`}>Other</span>
                  </label>
                  {otherBills && otherBills.length > 0 && (
                    <div className="flex items-center justify-between bg-emerald-50 rounded-lg px-2 py-1">
                      <p className="text-[9px] text-emerald-600 truncate flex-1">{otherBills.length} file(s)</p>
                      <button type="button" onClick={() => setOtherBills([])} className="text-[9px] text-red-500 ml-1">✕</button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* MULTIPLE INVOICES MODE - BALAJI or SVE with invoice_count=2 */}
          {isTwoInvoiceMode && (
            <>
              {/* Totals Summary */}
              <div className="p-2 bg-indigo-100 rounded-lg border border-indigo-300">
                <p className="text-[10px] font-semibold text-indigo-700">
                  Total: {formData.kgs || '0'} Kgs | {formData.tons || '0'} Tons | ₹{formData.invoice_amount || '0'}
                </p>
              </div>

              {/* Invoice 1 Section */}
              <div className="p-3 bg-blue-100 rounded-xl border border-blue-300">
                <div className="text-xs font-bold text-blue-800 mb-2 flex items-center gap-1">
                  <span className="w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px]">1</span>
                  Invoice 1
                  {formData.generated_invoice_file_1 && <span className="ml-auto text-emerald-600 text-[10px]">✓</span>}
                </div>
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <NavigableInput 
                      id="invoice-kgs-1"
                      type="number" 
                      value={formData.kgs_1} 
                      onChange={(e) => handleFieldChange('kgs_1', e.target.value)} 
                      onWheel={preventWheelChange} 
                      placeholder="Kgs *" 
                      className="h-10 rounded-lg text-sm"
                    />
                    <Input 
                      type="number" 
                      value={formData.tons_1 || ''} 
                      onChange={(e) => handleFieldChange('tons_1', e.target.value)} 
                      onWheel={preventWheelChange} 
                      placeholder="Tons" 
                      className="h-10 rounded-lg text-sm" 
                    />
                  </div>
                  <Input type="text" value={formData.invoice_amount_1 ? `₹${formData.invoice_amount_1}` : ''} readOnly placeholder="Amount" className="h-10 rounded-lg text-sm bg-slate-50" />
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <SimpleToggle checked={generate1Enabled} onChange={setGenerate1Enabled} color="blue" />
                      <span className="text-[10px] text-slate-600">Enable Generation</span>
                    </div>
                    <div className={`space-y-1 ${canUseManualInvoice ? '' : 'hidden'}`} data-testid="mobile-manual-invoice-wrapper-1">
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Manual Invoice No. 1 (optional)</p>
                    <Input
                      value={formData.manual_invoice_number_1 ?? ''}
                      onChange={(e) => {
                        handleFieldChange('manual_invoice_number_1', e.target.value);
                        if (clearManualInvoiceError) clearManualInvoiceError('manual_invoice_number_1');
                      }}
                      placeholder="e.g. MJ/SS25001"
                      className={`h-10 rounded-lg text-xs ${manualInvoiceErrors?.manual_invoice_number_1 ? 'border-red-500' : ''}`}
                      data-testid="mobile-manual-invoice-number-1"
                    />
                    {manualInvoiceErrors?.manual_invoice_number_1 && (
                      <p className="text-[10px] text-red-600 leading-tight">{manualInvoiceErrors.manual_invoice_number_1}</p>
                    )}
                    </div>
                    <div className="flex gap-1.5">
                      {!formData.generated_invoice_number_1 && (
                      <button type="button" onClick={peekNextInvoiceNumber} disabled={peeking} className="h-10 px-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 text-xs font-medium shrink-0" data-testid="mobile-peek-next-number-btn-1">
                        {peeking ? '...' : peekedNumber || '#?'}
                      </button>
                      )}
                      <button type="button" onClick={handleGenerateInvoice1Click} disabled={!generate1Enabled || !formData.product || !formData.kgs_1 || !formData.rate || generating1} className={`w-full h-10 rounded-lg text-sm font-semibold flex items-center justify-center gap-1 disabled:opacity-50 ${formData.generated_invoice_file_1 ? 'bg-green-500 text-white' : 'bg-red-400 text-white'}`}>
                        {generating1 ? <Loader2 className="w-4 h-4 animate-spin" /> : formData.generated_invoice_file_1 ? 'Regen INV1' : 'Gen INV1'}
                      </button>
                    </div>
                  </div>
                  {formData.generated_invoice_number_1 && (
                    <>
                      <div className="flex items-center gap-1 p-2 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-semibold text-green-800 truncate">{formData.generated_invoice_number_1}</p>
                          <p className="text-[9px] text-green-600">₹{formData.generated_invoice_amount_1}</p>
                        </div>
                        <div className="flex gap-1">
                          <a href={formData.generated_invoice_file_1} target="_blank" rel="noopener noreferrer" className="w-7 h-7 flex items-center justify-center bg-blue-500 text-white rounded" title="View"><Eye className="w-3 h-3" /></a>
                          <a href={formData.generated_invoice_file_1} download={`${formData.generated_invoice_number_1?.replace('PREVIEW-', '') || 'invoice1'}.pdf`} className="w-7 h-7 flex items-center justify-center bg-emerald-500 text-white rounded" title="Download"><Download className="w-3 h-3" /></a>
                          <button type="button" onClick={() => handleSendEmailClick(formData.generated_invoice_number_1, formData.generated_invoice_amount_1, 'regular')} className="w-7 h-7 flex items-center justify-center bg-purple-500 text-white rounded" title="Send Email"><Mail className="w-3 h-3" /></button>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <button type="button" onClick={() => { setInvoiceFile1(null); setFormData(prev => ({ ...prev, generated_invoice_file_1: '', generated_invoice_number_1: '', generated_invoice_amount_1: '' })); }} className="text-[9px] text-red-500 hover:text-red-700 underline">
                          Remove Generated Invoice
                        </button>
                      </div>
                    </>
                  )}
                  {/* File uploads for Invoice 1 */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-1">
                      <label className={`flex flex-col items-center justify-center h-14 rounded-lg border-2 border-dashed cursor-pointer ${invoiceFile1 || formData.generated_invoice_file_1 ? 'bg-emerald-50 border-emerald-400' : 'bg-red-50 border-red-300'}`}>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setInvoiceFile1(e.target.files?.[0] || null)} className="hidden" />
                        <FileText className={`w-4 h-4 ${invoiceFile1 || formData.generated_invoice_file_1 ? 'text-emerald-600' : 'text-red-500'}`} />
                        <span className={`text-[8px] mt-0.5 ${invoiceFile1 || formData.generated_invoice_file_1 ? 'text-emerald-600' : 'text-red-500'}`}>Invoice *</span>
                      </label>
                      {invoiceFile1 && (
                        <div className="flex items-center justify-between bg-emerald-50 rounded-lg px-1 py-0.5">
                          <p className="text-[8px] text-emerald-600 truncate flex-1">{invoiceFile1.name}</p>
                          <button type="button" onClick={() => { setInvoiceFile1(null); setFormData(prev => ({ ...prev, generated_invoice_file_1: '', generated_invoice_number_1: '', generated_invoice_amount_1: '' })); }} className="text-[8px] text-red-500 ml-1">✕</button>
                        </div>
                      )}
                      {!invoiceFile1 && formData.generated_invoice_file_1 && (
                        <div className="flex items-center justify-between bg-green-50 rounded-lg px-1 py-0.5">
                          <p className="text-[8px] text-green-600 truncate flex-1">{formData.generated_invoice_number_1 || 'Gen Inv 1'}</p>
                          <button type="button" onClick={() => setFormData(prev => ({ ...prev, generated_invoice_file_1: '', generated_invoice_number_1: '', generated_invoice_amount_1: '' }))} className="text-[8px] text-red-500 ml-1">✕</button>
                        </div>
                      )}
                    </div>
                    <div className="space-y-1">
                      <label className={`flex flex-col items-center justify-center h-14 rounded-lg border-2 border-dashed cursor-pointer ${permitFile1 ? 'bg-emerald-50 border-emerald-400' : 'bg-red-50 border-red-300'}`}>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setPermitFile1(e.target.files?.[0] || null)} className="hidden" />
                        <FileIcon className={`w-4 h-4 ${permitFile1 ? 'text-emerald-600' : 'text-red-500'}`} />
                        <span className={`text-[8px] mt-0.5 ${permitFile1 ? 'text-emerald-600' : 'text-red-500'}`}>Permit *</span>
                      </label>
                      {permitFile1 && (
                        <div className="flex items-center justify-between bg-emerald-50 rounded-lg px-1 py-0.5">
                          <p className="text-[8px] text-emerald-600 truncate flex-1">{permitFile1.name}</p>
                          <button type="button" onClick={() => setPermitFile1(null)} className="text-[8px] text-red-500 ml-1">✕</button>
                        </div>
                      )}
                    </div>
                    <div className="space-y-1">
                      <label className={`flex flex-col items-center justify-center h-14 rounded-lg border-2 border-dashed cursor-pointer ${otherBills1.length > 0 ? 'bg-emerald-50 border-emerald-400' : 'bg-slate-50 border-slate-300'}`}>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" multiple onChange={(e) => setOtherBills1(Array.from(e.target.files || []))} className="hidden" />
                        <Receipt className={`w-4 h-4 ${otherBills1.length > 0 ? 'text-emerald-600' : 'text-slate-400'}`} />
                        <span className={`text-[8px] mt-0.5 ${otherBills1.length > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>Other</span>
                      </label>
                      {otherBills1.length > 0 && (
                        <div className="flex items-center justify-between bg-emerald-50 rounded-lg px-1 py-0.5">
                          <p className="text-[8px] text-emerald-600 truncate flex-1">{otherBills1.length} file(s)</p>
                          <button type="button" onClick={() => setOtherBills1([])} className="text-[8px] text-red-500 ml-1">✕</button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Invoice 2 Section */}
              {(() => {
                const isInvoice1Complete = !!(formData.kgs_1 && (invoiceFile1 || formData.generated_invoice_file_1 || formData.invoice_file_url_1) && permitFile1);
                return (
              <div className={`p-3 rounded-xl border ${isInvoice1Complete ? 'bg-purple-100 border-purple-300' : 'bg-slate-100 border-slate-300 opacity-60'}`}>
                <div className="text-xs font-bold text-purple-800 mb-2 flex items-center gap-1">
                  <span className="w-5 h-5 rounded-full bg-purple-600 text-white flex items-center justify-center text-[10px]">2</span>
                  Invoice 2
                  {formData.generated_invoice_file_2 && <span className="ml-auto text-emerald-600 text-[10px]">✓</span>}
                </div>
                {!isInvoice1Complete && (
                  <p className="text-[10px] text-amber-600 mb-2">⚠️ Complete Invoice 1 details first (Kgs, Invoice File, Permit File)</p>
                )}
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <NavigableInput 
                      id="invoice-kgs-2"
                      type="number" 
                      value={formData.kgs_2} 
                      onChange={(e) => handleFieldChange('kgs_2', e.target.value)} 
                      onWheel={preventWheelChange} 
                      placeholder="Kgs *" 
                      className="h-10 rounded-lg text-sm"
                      disabled={!isInvoice1Complete}
                    />
                    <Input 
                      type="number" 
                      value={formData.tons_2 || ''} 
                      onChange={(e) => handleFieldChange('tons_2', e.target.value)} 
                      onWheel={preventWheelChange} 
                      placeholder="Tons" 
                      className="h-10 rounded-lg text-sm"
                      disabled={!isInvoice1Complete}
                    />
                  </div>
                  <Input type="text" value={formData.invoice_amount_2 ? `₹${formData.invoice_amount_2}` : ''} readOnly placeholder="Amount" className="h-10 rounded-lg text-sm bg-slate-50" />
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <SimpleToggle checked={generate2Enabled} onChange={setGenerate2Enabled} color="purple" disabled={!isInvoice1Complete} />
                      <span className="text-[10px] text-slate-600">Enable Generation</span>
                    </div>
                    <div className={`space-y-1 ${canUseManualInvoice ? '' : 'hidden'}`} data-testid="mobile-manual-invoice-wrapper-2">
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Manual Invoice No. 2 (optional)</p>
                    <Input
                      value={formData.manual_invoice_number_2 ?? ''}
                      onChange={(e) => {
                        handleFieldChange('manual_invoice_number_2', e.target.value);
                        if (clearManualInvoiceError) clearManualInvoiceError('manual_invoice_number_2');
                      }}
                      placeholder="e.g. MJ/SS25001"
                      className={`h-10 rounded-lg text-xs ${manualInvoiceErrors?.manual_invoice_number_2 ? 'border-red-500' : ''}`}
                      disabled={!isInvoice1Complete}
                      data-testid="mobile-manual-invoice-number-2"
                    />
                    {manualInvoiceErrors?.manual_invoice_number_2 && (
                      <p className="text-[10px] text-red-600 leading-tight">{manualInvoiceErrors.manual_invoice_number_2}</p>
                    )}
                    </div>
                    <div className="flex gap-1.5">
                      <button type="button" onClick={handleGenerateInvoice2Click} disabled={!generate2Enabled || !isInvoice1Complete || !formData.product || !formData.kgs_2 || !formData.rate || generating2} className={`w-full h-10 rounded-lg text-sm font-semibold flex items-center justify-center gap-1 disabled:opacity-50 ${formData.generated_invoice_file_2 ? 'bg-green-500 text-white' : 'bg-red-400 text-white'}`}>
                        {generating2 ? <Loader2 className="w-4 h-4 animate-spin" /> : formData.generated_invoice_file_2 ? 'Regen INV2' : 'Gen INV2'}
                      </button>
                    </div>
                  </div>
                  {formData.generated_invoice_number_2 && (
                    <>
                      <div className="flex items-center gap-1 p-2 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-semibold text-green-800 truncate">{formData.generated_invoice_number_2}</p>
                          <p className="text-[9px] text-green-600">₹{formData.generated_invoice_amount_2}</p>
                        </div>
                        <div className="flex gap-1">
                          <a href={formData.generated_invoice_file_2} target="_blank" rel="noopener noreferrer" className="w-7 h-7 flex items-center justify-center bg-blue-500 text-white rounded" title="View"><Eye className="w-3 h-3" /></a>
                          <a href={formData.generated_invoice_file_2} download={`${formData.generated_invoice_number_2?.replace('PREVIEW-', '') || 'invoice2'}.pdf`} className="w-7 h-7 flex items-center justify-center bg-emerald-500 text-white rounded" title="Download"><Download className="w-3 h-3" /></a>
                          <button type="button" onClick={() => handleSendEmailClick(formData.generated_invoice_number_2, formData.generated_invoice_amount_2, 'regular')} className="w-7 h-7 flex items-center justify-center bg-purple-500 text-white rounded" title="Send Email"><Mail className="w-3 h-3" /></button>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <button type="button" onClick={() => { setInvoiceFile2(null); setFormData(prev => ({ ...prev, generated_invoice_file_2: '', generated_invoice_number_2: '', generated_invoice_amount_2: '' })); }} className="text-[9px] text-red-500 hover:text-red-700 underline">
                          Remove Generated Invoice
                        </button>
                      </div>
                    </>
                  )}
                  {/* File uploads for Invoice 2 */}
                  <div className={`grid grid-cols-3 gap-2 ${!isInvoice1Complete ? 'pointer-events-none opacity-50' : ''}`}>
                    <div className="space-y-1">
                      <label className={`flex flex-col items-center justify-center h-14 rounded-lg border-2 border-dashed cursor-pointer ${invoiceFile2 || formData.generated_invoice_file_2 ? 'bg-emerald-50 border-emerald-400' : 'bg-red-50 border-red-300'}`}>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setInvoiceFile2(e.target.files?.[0] || null)} className="hidden" disabled={!isInvoice1Complete} />
                        <FileText className={`w-4 h-4 ${invoiceFile2 || formData.generated_invoice_file_2 ? 'text-emerald-600' : 'text-red-500'}`} />
                        <span className={`text-[8px] mt-0.5 ${invoiceFile2 || formData.generated_invoice_file_2 ? 'text-emerald-600' : 'text-red-500'}`}>Invoice *</span>
                      </label>
                      {invoiceFile2 && (
                        <div className="flex items-center justify-between bg-emerald-50 rounded-lg px-1 py-0.5">
                          <p className="text-[8px] text-emerald-600 truncate flex-1">{invoiceFile2.name}</p>
                          <button type="button" onClick={() => { setInvoiceFile2(null); setFormData(prev => ({ ...prev, generated_invoice_file_2: '', generated_invoice_number_2: '', generated_invoice_amount_2: '' })); }} className="text-[8px] text-red-500 ml-1">✕</button>
                        </div>
                      )}
                      {!invoiceFile2 && formData.generated_invoice_file_2 && (
                        <div className="flex items-center justify-between bg-green-50 rounded-lg px-1 py-0.5">
                          <p className="text-[8px] text-green-600 truncate flex-1">{formData.generated_invoice_number_2 || 'Gen Inv 2'}</p>
                          <button type="button" onClick={() => setFormData(prev => ({ ...prev, generated_invoice_file_2: '', generated_invoice_number_2: '', generated_invoice_amount_2: '' }))} className="text-[8px] text-red-500 ml-1">✕</button>
                        </div>
                      )}
                    </div>
                    <div className="space-y-1">
                      <label className={`flex flex-col items-center justify-center h-14 rounded-lg border-2 border-dashed cursor-pointer ${permitFile2 ? 'bg-emerald-50 border-emerald-400' : 'bg-red-50 border-red-300'}`}>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setPermitFile2(e.target.files?.[0] || null)} className="hidden" disabled={!isInvoice1Complete} />
                        <FileIcon className={`w-4 h-4 ${permitFile2 ? 'text-emerald-600' : 'text-red-500'}`} />
                        <span className={`text-[8px] mt-0.5 ${permitFile2 ? 'text-emerald-600' : 'text-red-500'}`}>Permit *</span>
                      </label>
                      {permitFile2 && (
                        <div className="flex items-center justify-between bg-emerald-50 rounded-lg px-1 py-0.5">
                          <p className="text-[8px] text-emerald-600 truncate flex-1">{permitFile2.name}</p>
                          <button type="button" onClick={() => setPermitFile2(null)} className="text-[8px] text-red-500 ml-1">✕</button>
                        </div>
                      )}
                    </div>
                    <div className="space-y-1">
                      <label className={`flex flex-col items-center justify-center h-14 rounded-lg border-2 border-dashed cursor-pointer ${otherBills2.length > 0 ? 'bg-emerald-50 border-emerald-400' : 'bg-slate-50 border-slate-300'}`}>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" multiple onChange={(e) => setOtherBills2(Array.from(e.target.files || []))} className="hidden" disabled={!isInvoice1Complete} />
                        <Receipt className={`w-4 h-4 ${otherBills2.length > 0 ? 'text-emerald-600' : 'text-slate-400'}`} />
                        <span className={`text-[8px] mt-0.5 ${otherBills2.length > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>Other</span>
                      </label>
                      {otherBills2.length > 0 && (
                        <div className="flex items-center justify-between bg-emerald-50 rounded-lg px-1 py-0.5">
                          <p className="text-[8px] text-emerald-600 truncate flex-1">{otherBills2.length} file(s)</p>
                          <button type="button" onClick={() => setOtherBills2([])} className="text-[8px] text-red-500 ml-1">✕</button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
                );
              })()}
            </>
          )}
        </>
      )}
    </div>
  );
}

function MobileUpdatedInvoiceSlide({ contextValue, locked }) {
  const {
    formData, setFormData, handleFieldChange,
    updatedInvoiceFile, setUpdatedInvoiceFile,
    generatingUpdated, handleGenerateUpdatedInvoiceClick,
    handleSendEmailClick,
  } = contextValue;
  
  const [generateUpdatedEnabled, setGenerateUpdatedEnabled] = useState(true);

  if (locked) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-slate-300 text-xs mb-2">
          <Lock className="w-3 h-3" />
          <span>This section is locked</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input value={formData.total_kgs_updated} readOnly placeholder="Updated Kgs" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
          <Input value={formData.total_tons_updated || ''} readOnly placeholder="Tons" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
        </div>
        <Input value={formData.updated_invoice_amount ? `₹${formData.updated_invoice_amount}` : ''} readOnly placeholder="Updated Amount" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80 font-semibold" />
        {formData.generated_updated_invoice_number && (
          <p className="text-xs text-slate-300">Invoice: {formData.generated_updated_invoice_number}</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <NavigableInput 
          id="updated-invoice-kgs"
          type="number" 
          value={formData.total_kgs_updated} 
          onChange={(e) => handleFieldChange('total_kgs_updated', e.target.value)} 
          onWheel={preventWheelChange} 
          placeholder="Updated Kgs *" 
          className="h-12 rounded-xl text-base"
          nextFieldId="updated-invoice-tons"
        />
        <NavigableInput 
          id="updated-invoice-tons"
          type="number" 
          value={formData.total_tons_updated || ''} 
          onChange={(e) => handleFieldChange('total_tons_updated', e.target.value)} 
          onWheel={preventWheelChange} 
          placeholder="Updated Tons" 
          className="h-12 rounded-xl text-base"
        />
      </div>
      <Input type="number" value={formData.updated_invoice_amount} readOnly placeholder="Updated Amount" className="h-12 rounded-xl text-base bg-slate-50 font-semibold" />
      
      {/* Generate Updated Invoice Button with Toggle */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <SimpleToggle checked={generateUpdatedEnabled} onChange={setGenerateUpdatedEnabled} color="purple" />
          <span className="text-xs text-slate-600">Enable Generation</span>
        </div>
        <button
          type="button"
          onClick={handleGenerateUpdatedInvoiceClick}
          disabled={!generateUpdatedEnabled || !formData.total_kgs_updated || !formData.rate || generatingUpdated}
          className={`w-full h-12 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md ${
            formData.generated_updated_invoice_file 
              ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white' 
              : 'bg-gradient-to-r from-red-400 to-red-500 hover:from-red-500 hover:to-red-600 text-white'
          }`}
        >
          {generatingUpdated ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Sparkles className="w-5 h-5" />
              <span>{formData.generated_updated_invoice_file ? 'Regen Upd INV' : 'Gen Upd INV'}</span>
            </>
          )}
        </button>
      </div>
      
      {/* Show generated updated invoice actions */}
      {formData.generated_updated_invoice_file && (
        <>
          <div className="flex items-center gap-2 p-3 bg-purple-100 rounded-xl border border-purple-300">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-purple-800 truncate">{formData.generated_updated_invoice_number}</p>
              <p className="text-[10px] text-purple-600">₹{formData.generated_updated_invoice_amount}</p>
            </div>
            <div className="flex gap-1.5">
              <a href={formData.generated_updated_invoice_file} target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors" title="View PDF">
                <Eye className="w-4 h-4" />
              </a>
              <a href={formData.generated_updated_invoice_file} download={`${formData.generated_updated_invoice_number?.replace('PREVIEW-', '') || 'updated_invoice'}.pdf`} className="w-9 h-9 flex items-center justify-center bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors" title="Download">
                <Download className="w-4 h-4" />
              </a>
              <button type="button" onClick={() => handleSendEmailClick(formData.generated_updated_invoice_number, formData.generated_updated_invoice_amount, 'updated')} className="w-9 h-9 flex items-center justify-center bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors" title="Send Email">
                <Mail className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex justify-end mt-1">
            <button type="button" onClick={() => { setUpdatedInvoiceFile(null); setFormData(prev => ({ ...prev, generated_updated_invoice_file: '', generated_updated_invoice_number: '', generated_updated_invoice_amount: '' })); }} className="text-[10px] text-red-500 hover:text-red-700 underline">
              Remove Generated Invoice
            </button>
          </div>
        </>
      )}
      
      {/* Updated Invoice File upload button */}
      <div className="space-y-1">
        <label className={`flex flex-col items-center justify-center h-16 rounded-xl border-2 border-dashed cursor-pointer transition-all ${updatedInvoiceFile || formData.generated_updated_invoice_file ? 'bg-emerald-50 border-emerald-400' : 'bg-red-50 border-red-300 hover:border-red-400'}`}>
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setUpdatedInvoiceFile(e.target.files[0])} className="hidden" />
          <FileText className={`w-5 h-5 ${updatedInvoiceFile || formData.generated_updated_invoice_file ? 'text-emerald-600' : 'text-red-500'}`} />
          <span className={`text-[10px] mt-1 ${updatedInvoiceFile || formData.generated_updated_invoice_file ? 'text-emerald-600' : 'text-red-500'}`}>
            Updated Invoice *
          </span>
        </label>
        {updatedInvoiceFile && (
          <div className="flex items-center justify-between bg-emerald-50 rounded-lg px-2 py-1">
            <p className="text-[9px] text-emerald-600 truncate flex-1">{updatedInvoiceFile.name}</p>
            <button type="button" onClick={() => { setUpdatedInvoiceFile(null); setFormData(prev => ({ ...prev, generated_updated_invoice_file: '', generated_updated_invoice_number: '', generated_updated_invoice_amount: '' })); }} className="text-[9px] text-red-500 ml-1">✕</button>
          </div>
        )}
        {!updatedInvoiceFile && formData.generated_updated_invoice_file && (
          <div className="flex items-center justify-between bg-green-50 rounded-lg px-2 py-1">
            <p className="text-[9px] text-green-600 truncate flex-1">{formData.generated_updated_invoice_number || 'Generated Updated Invoice'}</p>
            <button type="button" onClick={() => setFormData(prev => ({ ...prev, generated_updated_invoice_file: '', generated_updated_invoice_number: '', generated_updated_invoice_amount: '' }))} className="text-[9px] text-red-500 ml-1">✕</button>
          </div>
        )}
      </div>
    </div>
  );
}

function MobileWeightAdjustmentSlide({ contextValue, locked }) {
  const {
    formData, handleFieldChange,
  } = contextValue;

  if (locked) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-slate-300 text-xs mb-2">
          <Lock className="w-3 h-3" />
          <span>This section is locked</span>
        </div>
        <Input value={formData.weight_status === 'addition' ? 'Addition' : formData.weight_status === 'deduction' ? 'Deduction' : ''} readOnly placeholder="Weight Status" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
        {formData.weight_status === 'addition' && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <Input value={formData.additional_kgs || ''} readOnly placeholder="Additional Kgs" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
              <Input value={formData.additional_tons || ''} readOnly placeholder="Additional Tons" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
            </div>
            <Input value={formData.additional_invoice_amount ? `₹${formData.additional_invoice_amount}` : ''} readOnly placeholder="Addition Amount" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80 font-semibold" />
          </>
        )}
        {formData.weight_status === 'deduction' && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <Input value={formData.deduction_kgs || ''} readOnly placeholder="Deduction Kgs" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
              <Input value={formData.deduction_tons || ''} readOnly placeholder="Deduction Tons" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80" />
            </div>
            <Input value={formData.deduction_invoice_amount ? `₹${formData.deduction_invoice_amount}` : ''} readOnly placeholder="Deduction Amount" className="h-12 rounded-xl text-base bg-slate-700 border-slate-600 text-white opacity-80 font-semibold" />
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <span className="text-xs text-slate-500 ml-1">Weight Adjustment Type *</span>
        <Select value={formData.weight_status} onValueChange={(v) => handleFieldChange('weight_status', v)}>
          <SelectTrigger className={`h-12 rounded-xl text-base ${formData.weight_status ? 'border-slate-900' : ''}`}>
            <SelectValue placeholder="Select Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="addition">Addition</SelectItem>
            <SelectItem value="deduction">Deduction</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {formData.weight_status === 'addition' && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <span className="text-xs text-slate-500 ml-1">Addition Kgs *</span>
              <Input 
                type="number" 
                value={formData.additional_kgs} 
                onChange={(e) => handleFieldChange('additional_kgs', e.target.value)} 
                onWheel={preventWheelChange} 
                placeholder="0.00" 
                className={`h-12 rounded-xl text-base ${formData.additional_kgs ? 'border-emerald-500 bg-emerald-50' : ''}`}
              />
            </div>
            <div className="space-y-1">
              <span className="text-xs text-slate-500 ml-1">Tons *</span>
              <Input 
                type="number" 
                value={formData.additional_tons} 
                onChange={(e) => handleFieldChange('additional_tons', e.target.value)} 
                onWheel={preventWheelChange} 
                placeholder="0.000" 
                className={`h-12 rounded-xl text-base ${formData.additional_tons ? 'border-emerald-500 bg-emerald-50' : ''}`}
              />
            </div>
          </div>
          <div className="space-y-1">
            <span className="text-xs text-slate-500 ml-1">Amount *</span>
            <Input 
              type="number" 
              value={formData.additional_invoice_amount} 
              onChange={(e) => handleFieldChange('additional_invoice_amount', e.target.value)} 
              onWheel={preventWheelChange}
              placeholder="0.00" 
              className={`h-12 rounded-xl text-base ${formData.additional_invoice_amount ? 'border-emerald-500 bg-emerald-50' : ''}`}
            />
            <p className="text-[10px] text-slate-500 ml-1">Auto: rate x kgs + 5% GST</p>
          </div>
        </>
      )}
      
      {formData.weight_status === 'deduction' && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <span className="text-xs text-slate-500 ml-1">Deduction Kgs *</span>
              <Input 
                type="number" 
                value={formData.deduction_kgs} 
                onChange={(e) => handleFieldChange('deduction_kgs', e.target.value)} 
                onWheel={preventWheelChange} 
                placeholder="0.00" 
                className={`h-12 rounded-xl text-base ${formData.deduction_kgs ? 'border-emerald-500 bg-emerald-50' : ''}`}
              />
            </div>
            <div className="space-y-1">
              <span className="text-xs text-slate-500 ml-1">Tons *</span>
              <Input 
                type="number" 
                value={formData.deduction_tons} 
                onChange={(e) => handleFieldChange('deduction_tons', e.target.value)} 
                onWheel={preventWheelChange} 
                placeholder="0.000" 
                className={`h-12 rounded-xl text-base ${formData.deduction_tons ? 'border-emerald-500 bg-emerald-50' : ''}`}
              />
            </div>
          </div>
          <div className="space-y-1">
            <span className="text-xs text-slate-500 ml-1">Amount *</span>
            <Input 
              type="number" 
              value={formData.deduction_invoice_amount} 
              onChange={(e) => handleFieldChange('deduction_invoice_amount', e.target.value)} 
              onWheel={preventWheelChange}
              placeholder="0.00" 
              className={`h-12 rounded-xl text-base ${formData.deduction_invoice_amount ? 'border-emerald-500 bg-emerald-50' : ''}`}
            />
            <p className="text-[10px] text-slate-500 ml-1">Auto: rate x kgs + 5% GST</p>
          </div>
        </>
      )}
      
      {!formData.weight_status && (
        <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl">
          <p className="text-xs text-slate-600">Select Weight Adjustment Type to continue</p>
        </div>
      )}
    </div>
  );
}
